// src/components/staff/StaffDetails.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import {
    Typography, Button, Box, Paper, Divider,
    Avatar, Chip, Stack, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, InputAdornment, IconButton,
    Tabs, Tab, MenuItem, Fade, Tooltip, LinearProgress, Alert, Snackbar,
    Checkbox, FormControlLabel, Select, FormControl, InputLabel, Switch
} from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    Person as PersonIcon,
    Badge as BadgeIcon,
    Business as BusinessIcon,
    Email as EmailIcon,
    VpnKey as VpnKeyIcon,
    LocationOn as LocationOnIcon,
    VerifiedUser as VerifiedUserIcon,
    EventNote as EventNoteIcon,
    AssignmentInd as AssignmentIndIcon,
    WorkOutline as WorkIcon,
    LockReset as LockResetIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    RemoveRedEye as PreviewIcon,
    FileDownload as DownloadIcon,
    Description as DescriptionIcon,
    Assessment as AssessmentIcon,
    History as HistoryIcon,
    AccountBalance as BankIcon,
    Security as SecurityIcon,
    Category as CategoryIcon,
    Group as GroupIcon,
    ExitToApp as OffboardIcon,
    PersonAdd as RehireIcon,
    Print as PrintIcon,
    Close as CloseIcon,
    Warning as WarningIcon,
    CameraAlt as CameraAltIcon,
    Add as AddIcon
} from "@mui/icons-material";
import AppHeader from '../AppHeader';
import InfoItem from './components/InfoItem';
import RehireSearchModal from './components/RehireSearchModal';
import { formatDDMMYYYY } from "../../services/datetimeService";
import { getTextFieldStyle, toInputDate, buildHierarchicalList } from './constants';

const getRecurringElementStatus = (element, today = new Date()) => {
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const startKey = toInputDate(element?.effective_from);
    const endKey = toInputDate(element?.effective_to);

    if (endKey && endKey < todayKey) return { code: 'EXPIRED', label: 'Expired', color: 'default' };
    if (startKey && startKey > todayKey) return { code: 'SCHEDULED', label: 'Scheduled', color: 'info' };
    return { code: 'ACTIVE', label: 'Active', color: 'success' };
};

const calculateCumulativeTenure = (chain, currentStaff) => {
    if (!currentStaff) return { formatted: '0 Days', totalDays: 0 };
    // Only sum up to the current period's sequence
    const periods = (chain && chain.length > 0)
        ? chain.filter(p => p.employment_sequence <= (currentStaff.employment_sequence || 1))
        : [currentStaff];

    let totalDays = 0;

    for (const period of periods) {
        if (!period || !period.joining_date) continue;
        const start = new Date(period.joining_date);
        const isResigned = period.employment_status === 'RESIGNED' || period.employment_status === 'TERMINATED';
        const end = (isResigned && period.cessation_date)
            ? new Date(period.cessation_date)
            : new Date();

        const diffTime = end.getTime() - start.getTime();
        if (diffTime > 0) {
            totalDays += Math.floor(diffTime / (1000 * 60 * 60 * 24));
        }
    }

    if (totalDays <= 0) {
        return { formatted: '0 Days', totalDays: 0 };
    }

    const years = Math.floor(totalDays / 365);
    const remainingDaysAfterYears = totalDays % 365;
    const months = Math.floor(remainingDaysAfterYears / 30);
    const days = remainingDaysAfterYears % 30;

    const parts = [];
    if (years > 0) parts.push(`${years} Yr${years > 1 ? 's' : ''}`);
    if (months > 0) parts.push(`${months} Mo${months > 1 ? 's' : ''}`);
    if (days > 0 || parts.length === 0) parts.push(`${days} Day${days !== 1 ? 's' : ''}`);

    return {
        formatted: parts.join(' '),
        totalDays
    };
};

const calculateSingleTenure = (staff) => {
    if (!staff || !staff.joining_date) return { formatted: '0 Days', totalDays: 0 };

    const start = new Date(staff.joining_date);
    const isResigned = staff.employment_status === 'RESIGNED' || staff.employment_status === 'TERMINATED';
    const isNoticePeriod = staff.display_status === 'NOTICE_PERIOD';
    const end = (isResigned && staff.cessation_date)
        ? new Date(staff.cessation_date)
        : new Date();

    const diffTime = end.getTime() - start.getTime();
    let totalDays = 0;
    if (diffTime > 0) {
        totalDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    }

    if (totalDays <= 0) {
        return { formatted: '0 Days', totalDays: 0 };
    }

    const years = Math.floor(totalDays / 365);
    const remainingDaysAfterYears = totalDays % 365;
    const months = Math.floor(remainingDaysAfterYears / 30);
    const days = remainingDaysAfterYears % 30;

    const parts = [];
    if (years > 0) parts.push(`${years} Yr${years > 1 ? 's' : ''}`);
    if (months > 0) parts.push(`${months} Mo${months > 1 ? 's' : ''}`);
    if (days > 0 || parts.length === 0) parts.push(`${days} Day${days !== 1 ? 's' : ''}`);

    return {
        formatted: parts.join(' '),
        totalDays
    };
};

const timelineDateKey = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value.slice(0, 10);
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

const mergeAssignmentCompensation = (assignments = [], compensationEvents = []) => {
    const usedCompensationIds = new Set();
    const enrichedAssignments = assignments.map((assignment) => {
        const assignmentDate = timelineDateKey(assignment.effective_from);
        const assignmentReason = String(assignment.assignment_reason || '').toUpperCase();

        let event = compensationEvents.find((candidate) => (
            !usedCompensationIds.has(candidate.compensation_id) &&
            candidate.source_assignment_id &&
            Number(candidate.source_assignment_id) === Number(assignment.id) &&
            timelineDateKey(candidate.effective_from) === assignmentDate
        ));

        // Migrated starting compensation may predate source-assignment links.
        if (!event && ['HIRE', 'ONBOARDING'].includes(assignmentReason)) {
            event = compensationEvents.find((candidate) => (
                !usedCompensationIds.has(candidate.compensation_id) &&
                !candidate.source_assignment_id &&
                candidate.assignment_reason === 'INITIAL_SETUP' &&
                timelineDateKey(candidate.effective_from) === assignmentDate
            ));
        }

        // Older migrated timelines have no source_assignment_id. Merge only
        // when exactly one unlinked compensation event shares the assignment
        // effective date, so unrelated or ambiguous records stay separate.
        if (!event) {
            const sameDateUnlinked = compensationEvents.filter((candidate) => (
                !usedCompensationIds.has(candidate.compensation_id) &&
                !candidate.source_assignment_id &&
                timelineDateKey(candidate.effective_from) === assignmentDate
            ));
            if (sameDateUnlinked.length === 1) event = sameDateUnlinked[0];
        }

        if (event) usedCompensationIds.add(event.compensation_id);
        return event ? { ...assignment, compensation_event: event } : assignment;
    });

    return {
        assignments: enrichedAssignments,
        standaloneCompensation: compensationEvents.filter(
            (event) => !usedCompensationIds.has(event.compensation_id)
        )
    };
};

const StaffDetails = ({ setIsLoggedIn }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [staff, setStaff] = useState(null);
    const tenant = useMemo(() => localStorage.getItem("tenant") || "", []);
    const [currentUserRole, setCurrentUserRole] = useState(null);

    const [permissions, setPermissions] = useState([]);

    useEffect(() => {
        const storedUser = sessionStorage.getItem("user");
        if (storedUser) {
            const u = JSON.parse(storedUser);
            setCurrentUserRole(u.role);
            setPermissions(u.permissions || []);
        }
    }, []);

    const isPayrollAllowed = useMemo(() => {
        const r = currentUserRole?.toLowerCase();
        return permissions.includes("ACCESS_PAYROLL") || permissions.includes("VIEW_PAYROLL") || r === "super_admin" || r === "support_admin";
    }, [currentUserRole, permissions]);

    const isSalaryVisible = useMemo(() => {
        const r = currentUserRole?.toLowerCase();
        return permissions.includes("ACCESS_PAYROLL") || permissions.includes("VIEW_PAYROLL") || r === "super_admin" || r === "hr_manager";
    }, [currentUserRole, permissions]);

    const canViewContracts = useMemo(() => {
        const r = currentUserRole?.toLowerCase();
        return permissions.includes("ACCESS_PAYROLL") || permissions.includes("VIEW_PAYROLL") || r === "super_admin" || r === "support_admin" || r === "hr_manager";
    }, [currentUserRole, permissions]);

    // State
    const [somConfig, setSomConfig] = useState(null);
    const [orgNodes, setOrgNodes] = useState([]);
    const [orgTypes, setOrgTypes] = useState([]);
    const [jobRoles, setJobRoles] = useState([]);
    const [orgBranches, setOrgBranches] = useState([]);
    const [jobGrades, setJobGrades] = useState([]);
    const [openResetDialog, setOpenResetDialog] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);
    const [tabValue, setTabValue] = useState(0);

    // Offboard state
    const [offboardOpen, setOffboardOpen] = useState(false);
    const [offboardLoading, setOffboardLoading] = useState(false);
    const [offboardError, setOffboardError] = useState('');
    const [offboardForm, setOffboardForm] = useState({
        cessation_reason: 'RESIGNED',
        cessation_date: '',
        notes: '',
    });
    // Approver handover step
    const [handoverStep, setHandoverStep] = useState(false); // true = show step 2
    const [handoverList, setHandoverList] = useState([]); // affected_approvers from backend
    const [handoverAssignments, setHandoverAssignments] = useState({}); // { staff_id: new_user_id }
    const [handoverSaving, setHandoverSaving] = useState(false);
    const [allStaffList, setAllStaffList] = useState([]); // for approver dropdown
    const [pendingHandover, setPendingHandover] = useState([]); // pending approver impact (for re-entry)

    // Rehire state
    const [rehireSearchOpen, setRehireSearchOpen] = useState(false);
    const [assignmentHistory, setAssignmentHistory] = useState([]);
    const [compensationHistory, setCompensationHistory] = useState([]);
    const [employmentChain, setEmploymentChain] = useState([]); // Phase 3: cross-employment timeline
    const [documents, setDocuments] = useState([]);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [latePolicies, setLatePolicies] = useState([]);
    const [calcBases, setCalcBases] = useState([]);
    const [payrollElements, setPayrollElements] = useState([]);
    const [newDoc, setNewDoc] = useState({ type: 'NRC', notes: '', expiry_date: '', document_no: '', start_date: '', file: null, backFile: null });
    const [openHistoryEdit, setOpenHistoryEdit] = useState(false);
    const [selectedHistory, setSelectedHistory] = useState(null);
    const [openNewAssignment, setOpenNewAssignment] = useState(false);
    const [payrollFormData, setPayrollFormData] = useState({
        basic_salary: 0,
        calculation_basis_id: '',
        effective_from: null,
        recurring_elements: [],
        ssb_enabled: null,
        pit_enabled: null,
        ssb_scheme_override: 'INHERIT',
        statutory_reason: '',
        statutory_effective_from: null
    });
    const [openReportDialog, setOpenReportDialog] = useState(false);

    // Discipline states
    const [disciplineHistory, setDisciplineHistory] = useState([]);
    const [disciplineLookups, setDisciplineLookups] = useState(null);
    const [disciplinaryDialogOpen, setDisciplinaryDialogOpen] = useState(false);
    const [editingDisciplinary, setEditingDisciplinary] = useState(null);
    const [uploadDisciplineAttachmentOpen, setUploadDisciplineAttachmentOpen] = useState(false);
    const [selectedDisciplineForAttachment, setSelectedDisciplineForAttachment] = useState(null);
    const [disciplinaryForm, setDisciplinaryForm] = useState({
        discipline_id: '',
        severe_level_id: '',
        case_desc: '',
        case_date: '',
        officer_id: '',
        dsoutcome_id: '',
        remark: '',
        file: null
    });

    // Family states
    const [familyMembers, setFamilyMembers] = useState([]);
    const [familyDialogOpen, setFamilyDialogOpen] = useState(false);
    const [editingFamily, setEditingFamily] = useState(null);
    const [costCentres, setCostCentres] = useState([]);
    const [costCentreAllocations, setCostCentreAllocations] = useState([]);
    const [openCostCentreDialog, setOpenCostCentreDialog] = useState(false);
    const [ccAllocationsForm, setCcAllocationsForm] = useState([]);
    const [statusMsg, setStatusMsg] = useState({ open: false, msg: '', severity: 'success' });
    const [familyForm, setFamilyForm] = useState({
        member_name: '',
        relationship: '',
        dob: '',
        salutation: '',
        gender: '',
        marital_status: '',
        is_working: false,
        is_studying: false,
        notes: ''
    });

    const singleTenure = useMemo(() => {
        return calculateSingleTenure(staff);
    }, [staff]);

    const cumulativeTenure = useMemo(() => {
        return calculateCumulativeTenure(employmentChain, staff);
    }, [employmentChain, staff]);

    const compensationMilestones = useMemo(() => {
        const sortedCompensation = [...(compensationHistory || [])].sort(
            (a, b) => new Date(a.effective_from) - new Date(b.effective_from) || Number(a.id) - Number(b.id)
        );

        return sortedCompensation.map((item, index) => {
            const reason = String(item.payroll_change_reason || '').toUpperCase();
            const previous = index > 0 ? sortedCompensation[index - 1] : null;

            return {
                id: `compensation-${item.id}`,
                compensation_id: item.id,
                source_assignment_id: item.source_assignment_id,
                event_type: 'COMPENSATION',
                assignment_reason: reason || (index === 0 ? 'INITIAL_SETUP' : 'COMPENSATION_CHANGE'),
                effective_from: item.effective_from,
                effective_to: item.effective_to,
                previous_salary: previous?.basic_salary ?? null,
                basic_salary: item.basic_salary
            };
        }).filter(Boolean);
    }, [compensationHistory]);

    const careerTimeline = useMemo(() => {
        const merged = mergeAssignmentCompensation(assignmentHistory || [], compensationMilestones);
        return [...merged.assignments, ...merged.standaloneCompensation].sort((a, b) => (
            new Date(b.effective_from) - new Date(a.effective_from)
        ));
    }, [assignmentHistory, compensationMilestones]);

    const unifiedTimeline = useMemo(() => {
        if (employmentChain && employmentChain.length > 0) {
            const list = [];
            employmentChain.forEach(period => {
                const seq = period.employment_sequence || 1;
                const code = period.employee_number || period.staff_code || '';
                if (Array.isArray(period.assignments)) {
                    period.assignments.forEach(a => {
                        list.push({
                            ...a,
                            employment_sequence: seq,
                            employee_number: code
                        });
                    });
                }
            });
            const merged = mergeAssignmentCompensation(list, compensationMilestones);
            merged.standaloneCompensation.forEach((event) => {
                merged.assignments.push({
                    ...event,
                    employment_sequence: staff?.employment_sequence || 1,
                    employee_number: staff?.employee_number || staff?.staff_code || ''
                });
            });
            return merged.assignments.sort((a, b) => {
                const dateA = new Date(a.effective_from);
                const dateB = new Date(b.effective_from);
                return dateB - dateA;
            });
        } else {
            const merged = mergeAssignmentCompensation(assignmentHistory || [], compensationMilestones);
            return [...merged.assignments, ...merged.standaloneCompensation].map(a => ({
                ...a,
                employment_sequence: staff?.employment_sequence || 1,
                employee_number: staff?.employee_number || staff?.staff_code || ''
            }));
        }
    }, [employmentChain, assignmentHistory, compensationMilestones, staff]);

    const fetchData = useCallback(async () => {
        try {
            const h = { "X-Tenant": tenant };

            // 1. Fetch core data first (always allowed for staff view)
            const [staffRes, configRes, nodesRes, typesRes, histRes, docsRes, rolesRes, branchesRes, gradesRes, disciplineRes, lookupsRes, familyRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_STAFF_API_URL}/staff/${id}`, { withCredentials: true, headers: h }),
                axios.get(`${process.env.REACT_APP_API_URL1}/som/config`, { withCredentials: true, headers: h }),
                axios.get(`${process.env.REACT_APP_API_URL1}/som/nodes`, { withCredentials: true, headers: h }),
                axios.get(`${process.env.REACT_APP_API_URL1}/som/types`, { withCredentials: true, headers: h }),
                axios.get(`${process.env.REACT_APP_STAFF_API_URL}/staff/${id}/history`, { withCredentials: true, headers: h }),
                axios.get(`${process.env.REACT_APP_API_URL1}/personnel/staff/${id}/documents`, { withCredentials: true, headers: h }),
                axios.get(`${process.env.REACT_APP_API_URL1}/som/roles`, { withCredentials: true, headers: h }),
                axios.get(`${process.env.REACT_APP_API_URL1}/som/branches`, { withCredentials: true, headers: h }),
                axios.get(`${process.env.REACT_APP_API_URL1}/som/grades`, { withCredentials: true, headers: h }),
                axios.get(`${process.env.REACT_APP_STAFF_API_URL}/staff/${id}/discipline`, { withCredentials: true, headers: h }),
                axios.get(`${process.env.REACT_APP_STAFF_API_URL}/staff-import/discipline/lookups`, { withCredentials: true, headers: h }),
                axios.get(`${process.env.REACT_APP_STAFF_API_URL}/staff/${id}/family`, { withCredentials: true, headers: h })
            ]);

            const sData = staffRes.data;
            const docs = docsRes.data?.data || [];
            const photoDoc = docs.find(d => d.document_type === 'PROFILE_PHOTO');
            const token = sessionStorage.getItem("token") || "";
            const photoUrl = photoDoc ? `${process.env.REACT_APP_API_URL1}/admin/documents/${photoDoc.dms_id}/preview?tenant=${tenant}&token=${token}` : "/default-avatar.png";

            const locs = Array.isArray(sData.allowed_locations) ? sData.allowed_locations : (sData.allowed_locations ? sData.allowed_locations.split(",").map(s => s.trim()) : []);
            setStaff({ ...sData, allowed_locations: locs, photo_url: photoUrl });

            if (configRes.data.success) {
                let cfg = configRes.data.data;
                if (typeof cfg.active_levels_json === "string") cfg.active_levels_json = JSON.parse(cfg.active_levels_json);
                setSomConfig(cfg);
            }
            if (nodesRes.data.success) setOrgNodes(buildHierarchicalList(nodesRes.data.data));
            if (typesRes.data.success) setOrgTypes(typesRes.data.data);
            if (histRes.data.data) setAssignmentHistory(histRes.data.data);
            if (docsRes.data.success) setDocuments(docsRes.data.data);
            if (rolesRes.data.success) setJobRoles(rolesRes.data.data);
            if (branchesRes.data.success) setOrgBranches(branchesRes.data.data);
            if (gradesRes.data.success) setJobGrades(gradesRes.data.data);
            if (disciplineRes.data.success) setDisciplineHistory(disciplineRes.data.data);
            if (lookupsRes.data.success) setDisciplineLookups(lookupsRes.data.data);
            if (familyRes.data.success) setFamilyMembers(familyRes.data.data);

            // 1b. Fetch employment chain (for rehires — sequence > 1)
            // We always fetch it; if only 1 record in chain, the UI hides the section
            try {
                const chainRes = await axios.get(
                    `${process.env.REACT_APP_STAFF_API_URL}/staff/${id}/employment-chain`,
                    { withCredentials: true, headers: h }
                );
                if (chainRes.data.success) setEmploymentChain(chainRes.data.data);
            } catch (chainErr) {
                console.warn('Employment chain fetch failed:', chainErr.message);
            }

            // 1c. If staff is in Notice Period / Resigned, check for pending approver reassignments
            const displayStatus = sData.display_status || sData.employment_status;
            if (displayStatus === 'NOTICE_PERIOD' || displayStatus === 'RESIGNED') {
                try {
                    const impactRes = await axios.get(
                        `${process.env.REACT_APP_STAFF_API_URL}/staff/${id}/approver-impact`,
                        { withCredentials: true, headers: h }
                    );
                    if (impactRes.data.success && impactRes.data.affected_approvers?.length > 0) {
                        setPendingHandover(impactRes.data.affected_approvers);
                    } else {
                        setPendingHandover([]);
                    }
                } catch (impErr) {
                    console.warn('approver-impact check failed:', impErr?.response?.status, impErr?.response?.data || impErr.message);
                }
            }

            // 2. Fetch payroll data only if allowed
            if (isPayrollAllowed) {
                try {
                    const [lpRes, cbRes, peRes, sPayRes] = await Promise.all([
                        axios.get(`${process.env.REACT_APP_API_URL1}/sm/late-policies`, { withCredentials: true, headers: h }),
                        axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/calc-bases`, { withCredentials: true, headers: h }),
                        axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/elements`, { withCredentials: true, headers: h }),
                        axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/staff/${id}`, { withCredentials: true, headers: h })
                    ]);

                    if (lpRes.data.success) setLatePolicies(lpRes.data.data);
                    if (cbRes.data.success) setCalcBases(cbRes.data.data);
                    if (peRes.data.success) setPayrollElements(peRes.data.data);
                    if (sPayRes.data.success && sPayRes.data.data) {
                        const pay = sPayRes.data.data;
                        setCompensationHistory(pay.history || []);
                        setPayrollFormData({
                            basic_salary: pay.profile?.basic_salary || 0,
                            calculation_basis_id: pay.profile?.calculation_basis_id || '',
                            effective_from: pay.profile?.effective_from || null,
                            recurring_elements: pay.recurring || [],
                            ssb_enabled: pay.statutory?.ssb_enabled ?? null,
                            pit_enabled: pay.statutory?.pit_enabled ?? null,
                            ssb_scheme_override: pay.statutory?.ssb_scheme_override || 'INHERIT',
                            statutory_reason: pay.statutory?.reason || '',
                            statutory_effective_from: pay.statutory?.effective_from || null
                        });
                    }
                } catch (pErr) {
                    console.warn("Payroll data fetch failed (likely permission restricted):", pErr.message);
                }
            }
        } catch (err) {
            console.error("Staff data fetch error:", err);
            // Don't leave it loading forever
            if (!staff) setStaff({ error: true });
        }
    }, [id, tenant, isPayrollAllowed]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        const fetchCCData = async () => {
            if (!isPayrollAllowed) return;
            try {
                const h = { "X-Tenant": tenant };
                const ccRes = await axios.get(`${process.env.REACT_APP_API_URL1}/som/cost-centres`, { withCredentials: true, headers: h });
                if (ccRes.data.success) {
                    setCostCentres(ccRes.data.data.filter(c => c.is_active === 1 || c.is_active === true));
                }

                if (assignmentHistory && assignmentHistory.length > 0) {
                    const currentAssignmentId = assignmentHistory[0].id;
                    const allocRes = await axios.get(`${process.env.REACT_APP_API_URL1}/som/cost-centre-allocations/${currentAssignmentId}`, { withCredentials: true, headers: h });
                    if (allocRes.data.success) {
                        setCostCentreAllocations(allocRes.data.data);
                    }
                }
            } catch (err) {
                console.warn('Cost centre data fetch failed:', err.message);
            }
        };
        fetchCCData();
    }, [isPayrollAllowed, assignmentHistory, tenant]);

    const handleSaveAllocations = async () => {
        const total = ccAllocationsForm.reduce((sum, item) => sum + Number(item.allocation_percentage || 0), 0);
        if (total !== 100) {
            setStatusMsg({ open: true, msg: 'Total allocation percentage must equal 100%.', severity: 'warning' });
            return;
        }

        const currentAssignmentId = assignmentHistory[0]?.id;
        if (!currentAssignmentId) return;

        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL1}/som/cost-centre-allocations/${currentAssignmentId}`, {
                allocations: ccAllocationsForm
            }, { withCredentials: true, headers: { "X-Tenant": tenant } });

            if (res.data.success) {
                setCostCentreAllocations(res.data.data);
                setOpenCostCentreDialog(false);
                setStatusMsg({ open: true, msg: 'Cost centre allocations updated successfully', severity: 'success' });
            }
        } catch (err) {
            setStatusMsg({ open: true, msg: err.response?.data?.error || err.message, severity: 'error' });
        }
    };

    const handleResetPassword = async () => {
        if (!newPassword || newPassword.length < 6) return;
        setResetLoading(true);
        try {
            await axios.put(`${process.env.REACT_APP_STAFF_API_URL}/staff/${id}`, { ...staff, password: newPassword }, { withCredentials: true, headers: { "X-Tenant": tenant } });
            setOpenResetDialog(false);
            setNewPassword('');
        } catch (err) { console.error(err); } finally { setResetLoading(false); }
    };

    const handleOffboard = async () => {
        if (!offboardForm.cessation_date) {
            setOffboardError('Please select the last working day.');
            return;
        }
        setOffboardLoading(true);
        setOffboardError('');
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_STAFF_API_URL}/staff/${id}/offboard`,
                offboardForm,
                { withCredentials: true, headers: { "X-Tenant": tenant } }
            );
            const data = res.data;
            if (data.approver_handover_required && data.affected_approvers?.length > 0) {
                // Step 2: show handover panel
                setHandoverList(data.affected_approvers);
                const initAssign = {};
                data.affected_approvers.forEach(a => { initAssign[a.staff_id] = ''; });
                setHandoverAssignments(initAssign);
                setHandoverStep(true);
                // Fetch all eligible approvers for dropdown (all active users, any role)
                if (allStaffList.length === 0) {
                    try {
                        const candidatesRes = await axios.get(
                            `${process.env.REACT_APP_STAFF_API_URL}/staff/approver-candidates`,
                            { withCredentials: true, headers: { "X-Tenant": tenant } }
                        );
                        setAllStaffList(candidatesRes.data?.data || []);
                    } catch { /* non-critical */ }
                }
            } else {
                // No handover needed — close and go back
                setOffboardOpen(false);
                navigate('/staff');
            }
        } catch (err) {
            setOffboardError(err?.response?.data?.message || 'Failed to offboard staff. Please try again.');
        } finally {
            setOffboardLoading(false);
        }
    };

    const handleHandoverSave = async () => {
        const assignments = handoverList
            .filter(a => handoverAssignments[a.staff_id])
            .map(a => ({
                staff_id: a.staff_id,
                approver_field: a.approver_field,
                new_user_id: handoverAssignments[a.staff_id],
            }));

        setHandoverSaving(true);
        try {
            if (assignments.length > 0) {
                await axios.post(
                    `${process.env.REACT_APP_STAFF_API_URL}/staff/${id}/reassign-approvers`,
                    { assignments },
                    { withCredentials: true, headers: { "X-Tenant": tenant } }
                );
            }
            setOffboardOpen(false);
            setHandoverStep(false);
            setPendingHandover([]); // clear banner — all done
            navigate('/staff');
        } catch (err) {
            setOffboardError(err?.response?.data?.message || 'Failed to save reassignments.');
        } finally {
            setHandoverSaving(false);
        }
    };

    const handleBulkAssign = (newUserId) => {
        const updated = {};
        handoverList.forEach(a => { updated[a.staff_id] = newUserId; });
        setHandoverAssignments(updated);
    };

    const handlePrintReport = () => {
        if (!staff) return;

        const timelineRows = unifiedTimeline.map((item, index) => {
            const dateStr = `${formatDDMMYYYY(item.effective_from)} - ${item.effective_to ? formatDDMMYYYY(item.effective_to) : '-'}`;
            const salaryCell = isSalaryVisible
                ? `<td class="text-right font-semibold">${item.basic_salary > 0 ? Number(item.basic_salary).toLocaleString() + ' MMK' : '—'}</td>`
                : '';
            // is_active = 0 means this was a same-day correction superseded by a newer record
            const isSuperseded = item.is_active === 0;
            const rowStyle = isSuperseded ? 'opacity:0.45; font-style:italic;' : '';
            const supersededBadge = isSuperseded
                ? `<span style="font-size:9px; background:#e2e8f0; color:#64748b; border-radius:3px; padding:1px 5px; margin-left:4px;">CORRECTED</span>`
                : '';
            return `
                <tr style="${rowStyle}">
                    <td class="font-bold">${item.employee_number || ''}${supersededBadge}</td>
                    <td><span class="badge badge-seq">Period #${item.employment_sequence || 1}</span></td>
                    <td>${dateStr}</td>
                    <td class="font-semibold">${item.job_role_name || item.role_name || '—'}</td>
                    <td>${item.org_node_name || '—'}</td>
                    <td>${item.branch_name || '—'}</td>
                    <td>
                        <span class="badge ${item.assignment_reason === 'PROMOTION' ? 'badge-success' :
                    item.assignment_reason === 'TRANSFER' ? 'badge-info' :
                        item.assignment_reason === 'REHIRE' ? 'badge-warning' : 'badge-default'
                }">${item.assignment_reason || 'ONBOARDING'}</span>
                    </td>
                    ${salaryCell}
                </tr>
            `;
        }).join('');

        const chainSummary = (employmentChain && employmentChain.length > 0) ? employmentChain.map((p, pIdx) => {
            const isCurrent = p.staff_id === Number(id);
            const statusClass = p.employment_status === 'ACTIVE' ? 'badge-success' :
                p.employment_status === 'RESIGNED' ? 'badge-warning' : 'badge-danger';
            return `
                <div class="chain-period ${isCurrent ? 'current-period' : ''}">
                    <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom: 5px;">
                        <h4 style="margin:0; font-weight:800; font-size:14px; color:#1e293b;">
                            Employment Period #${p.employment_sequence || pIdx + 1}
                            ${isCurrent ? '<span class="badge badge-primary">Current</span>' : ''}
                        </h4>
                        <span class="badge ${statusClass}">${p.employment_status}</span>
                    </div>
                    <div style="font-size:12px; color:#64748b; font-weight:600;">
                        <span>ID: ${p.employee_number || p.staff_code}</span>
                        <span style="margin: 0 10px;">•</span>
                        <span>Joined: ${p.joining_date ? formatDDMMYYYY(p.joining_date) : '—'}</span>
                        ${p.cessation_date ? `
                            <span style="margin: 0 10px;">•</span>
                            <span>Left: ${formatDDMMYYYY(p.cessation_date)} (${p.cessation_reason || 'RESIGNED'})</span>
                        ` : ''}
                    </div>
                    ${p.job_role_name ? `
                        <div style="font-size:12px; color:#475569; margin-top: 3px; font-weight:500;">
                            Role: ${p.job_role_name} ${p.branch_name ? `(${p.branch_name})` : ''}
                        </div>
                    ` : ''}
                </div>
            `;
        }).join('') : '';

        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('Popup blocker prevented opening the print report window. Please allow popups for this site.');
            return;
        }

        const isResigned = staff.employment_status === 'RESIGNED' || staff.employment_status === 'TERMINATED';
        const statusBadgeClass = staff.employment_status === 'ACTIVE' ? 'badge-success' :
            staff.employment_status === 'RESIGNED' ? 'badge-warning' : 'badge-danger';

        const printHtml = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <title>Staff Profile Report - ${staff.full_name}</title>
                <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Inter:wght@300;400;500;600;700;800&display=swap" rel="stylesheet">
                <style>
                    body {
                        font-family: 'Inter', -apple-system, sans-serif;
                        color: #0f172a;
                        line-height: 1.5;
                        padding: 30px;
                        max-width: 950px;
                        margin: 0 auto;
                        background: #fff;
                    }
                    h1, h2, h3, h4 {
                        font-family: 'Outfit', sans-serif;
                        color: #0f172a;
                        margin: 0;
                    }
                    .no-print-btn {
                        display: flex;
                        justify-content: flex-end;
                        margin-bottom: 20px;
                    }
                    .btn {
                        background-color: #6366f1;
                        color: white;
                        border: none;
                        padding: 8px 16px;
                        font-weight: 700;
                        border-radius: 6px;
                        cursor: pointer;
                        font-family: 'Inter', sans-serif;
                        font-size: 13px;
                    }
                    .btn:hover {
                        background-color: #4f46e5;
                    }
                    .header {
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                        border-bottom: 2px solid #f1f5f9;
                        padding-bottom: 20px;
                        margin-bottom: 25px;
                    }
                    .header-title h1 {
                        font-size: 26px;
                        font-weight: 900;
                        color: #4f46e5;
                        letter-spacing: -0.02em;
                    }
                    .header-title p {
                        margin: 5px 0 0 0;
                        color: #64748b;
                        font-size: 13px;
                        font-weight: 600;
                    }
                    .meta-info {
                        text-align: right;
                        font-size: 11px;
                        color: #64748b;
                        font-weight: 500;
                    }
                    .badge {
                        display: inline-block;
                        padding: 2px 7px;
                        font-size: 10px;
                        font-weight: 800;
                        border-radius: 5px;
                        text-transform: uppercase;
                    }
                    .badge-success { background-color: #dcfce7; color: #16a34a; }
                    .badge-warning { background-color: #fef3c7; color: #d97706; }
                    .badge-danger { background-color: #fef2f2; color: #dc2626; }
                    .badge-primary { background-color: #e0e7ff; color: #4338ca; }
                    .badge-info { background-color: #ede9fe; color: #6d28d9; }
                    .badge-seq { background-color: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; }
                    .badge-default { background-color: #f1f5f9; color: #64748b; }
                    .section {
                        margin-bottom: 30px;
                        page-break-inside: avoid;
                    }
                    .section-title {
                        font-size: 14px;
                        font-weight: 800;
                        text-transform: uppercase;
                        letter-spacing: 0.05em;
                        color: #475569;
                        border-bottom: 1.5px solid #f1f5f9;
                        padding-bottom: 6px;
                        margin-bottom: 15px;
                    }
                    .grid {
                        display: grid;
                        grid-template-columns: repeat(3, 1fr);
                        gap: 15px 20px;
                    }
                    .info-group {
                        display: flex;
                        flex-direction: column;
                    }
                    .info-label {
                        font-size: 10px;
                        font-weight: 800;
                        text-transform: uppercase;
                        color: #94a3b8;
                        letter-spacing: 0.03em;
                        margin-bottom: 3px;
                    }
                    .info-value {
                        font-size: 13px;
                        font-weight: 700;
                        color: #334155;
                    }
                    .table-container {
                        margin-top: 10px;
                        border: 1px solid #e2e8f0;
                        border-radius: 8px;
                        overflow: hidden;
                    }
                    .table {
                        width: 100%;
                        border-collapse: collapse;
                    }
                    .table th {
                        background-color: #f8fafc;
                        color: #475569;
                        text-align: left;
                        padding: 10px 12px;
                        font-size: 10px;
                        font-weight: 800;
                        text-transform: uppercase;
                        border-bottom: 1px solid #e2e8f0;
                    }
                    .table td {
                        padding: 10px 12px;
                        font-size: 12px;
                        color: #334155;
                        border-bottom: 1px solid #f1f5f9;
                    }
                    .table tr:last-child td {
                        border-bottom: none;
                    }
                    .text-right {
                        text-align: right;
                    }
                    .font-semibold {
                        font-weight: 600;
                    }
                    .font-bold {
                        font-weight: 700;
                    }
                    .chain-container {
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                        margin-top: 10px;
                    }
                    .chain-period {
                        border: 1.5px solid #f1f5f9;
                        border-radius: 8px;
                        padding: 12px 15px;
                        background: #fafbff;
                    }
                    .current-period {
                        border-color: #cbd5e1;
                        background: #f8fafc;
                    }
                    @media print {
                        body {
                            padding: 0;
                        }
                        .no-print {
                            display: none;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="no-print no-print-btn">
                    <button class="btn" onclick="window.print()">Print Document</button>
                </div>

                <div class="header">
                    <div class="header-title">
                        <h1>Staff Profile Report</h1>
                        <p>${(staff.salutation ? `${staff.salutation} ` : '') + staff.full_name} (${staff.employee_number || staff.staff_code || ''})</p>
                    </div>
                    <div class="meta-info">
                        <div>Report Generated: ${new Date().toLocaleString()}</div>
                        <div style="margin-top: 4px;">Tenant: ${tenant.toUpperCase()}</div>
                    </div>
                </div>

                <div class="section">
                    <div class="section-title">Personal Summary</div>
                    <div class="grid">
                        <div class="info-group">
                            <span class="info-label">Full Name</span>
                            <span class="info-value">${(staff.salutation ? `${staff.salutation} ` : '') + staff.full_name}</span>
                        </div>
                        <div class="info-group">
                            <span class="info-label">Current Employee ID</span>
                            <span class="info-value">${staff.employee_number || staff.staff_code || ''}</span>
                        </div>
                        <div class="info-group">
                            <span class="info-label">Employment Status</span>
                            <span class="info-value">
                                <span class="badge ${statusBadgeClass}">${staff.employment_status}</span>
                                ${staff.employment_sequence > 1 ? `<span class="badge badge-info" style="margin-left:5px;">Rehire #${staff.employment_sequence}</span>` : ''}
                            </span>
                        </div>
                        <div class="info-group">
                            <span class="info-label">Tenure (This Period)</span>
                            <span class="info-value">${singleTenure.formatted}</span>
                        </div>
                        ${staff.employment_sequence > 1 ? `
                            <div class="info-group">
                                <span class="info-label">Cumulative Tenure</span>
                                <span class="info-value">${cumulativeTenure.formatted}</span>
                            </div>
                        ` : ''}
                        <div class="info-group">
                            <span class="info-label">Joining Date</span>
                            <span class="info-value">${staff.joining_date ? formatDDMMYYYY(staff.joining_date) : '—'}</span>
                        </div>
                        ${isResigned ? `
                            <div class="info-group">
                                <span class="info-label">Cessation Date</span>
                                <span class="info-value">${staff.cessation_date ? formatDDMMYYYY(staff.cessation_date) : '—'}</span>
                            </div>
                        ` : `
                            <div class="info-group">
                                <span class="info-label">Current Role</span>
                                <span class="info-value">${staff.job_role_name || '—'}</span>
                            </div>
                        `}
                    </div>
                </div>

                <div class="section">
                    <div class="section-title">Demographics & Identity</div>
                    <div class="grid">
                        <div class="info-group">
                            <span class="info-label">Gender</span>
                            <span class="info-value">${staff.gender ? staff.gender.charAt(0).toUpperCase() + staff.gender.slice(1).toLowerCase() : '—'}</span>
                        </div>
                        <div class="info-group">
                            <span class="info-label">Date of Birth</span>
                            <span class="info-value">${staff.date_of_birth ? formatDDMMYYYY(staff.date_of_birth) : '—'}</span>
                        </div>
                        <div class="info-group">
                            <span class="info-label">Father's Name</span>
                            <span class="info-value">${staff.father_name || '—'}</span>
                        </div>
                        <div class="info-group">
                            <span class="info-label">NRC Number (EN)</span>
                            <span class="info-value">${staff.nrc_number_english || staff.nrc_number || '—'}</span>
                        </div>
                        <div class="info-group">
                            <span class="info-label">NRC Number (MM)</span>
                            <span class="info-value">${staff.nrc_number_myanmar || '—'}</span>
                        </div>
                        <div class="info-group">
                            <span class="info-label">Passport</span>
                            <span class="info-value">${staff.passport_number || '—'}</span>
                        </div>
                        <div class="info-group">
                            <span class="info-label">Social Security No</span>
                            <span class="info-value">${staff.social_security_no || '—'}</span>
                        </div>
                        <div class="info-group">
                            <span class="info-label">Nationality</span>
                            <span class="info-value">${staff.nationality || 'Burmese'}</span>
                        </div>
                        <div class="info-group">
                            <span class="info-label">Marital Status</span>
                            <span class="info-value">${staff.marital_status ? staff.marital_status.charAt(0).toUpperCase() + staff.marital_status.slice(1).toLowerCase() : '—'}</span>
                        </div>
                    </div>
                </div>

                <div class="section">
                    <div class="section-title">Contact & Addresses</div>
                    <div class="grid">
                        <div class="info-group" style="grid-column: span 2;">
                            <span class="info-label">Permanent Address</span>
                            <span class="info-value">${staff.permanent_address || '—'}</span>
                        </div>
                        <div class="info-group">
                            <span class="info-label">Personal Phone</span>
                            <span class="info-value">${staff.personal_phone || '—'}</span>
                        </div>
                        <div class="info-group">
                            <span class="info-label">Emergency Contact Name</span>
                            <span class="info-value">${staff.emergency_contact_name || '—'}</span>
                        </div>
                        <div class="info-group">
                            <span class="info-label">Emergency Phone</span>
                            <span class="info-value">${staff.emergency_contact_phone || '—'}</span>
                        </div>
                        <div class="info-group">
                            <span class="info-label">Primary Bank Account</span>
                            <span class="info-value">${staff.bank_account_no ? `${staff.bank_account_no} (${staff.bank_name || ''})` : '—'}</span>
                        </div>
                    </div>
                </div>

                ${chainSummary ? `
                    <div class="section">
                        <div class="section-title">Employment Chain History</div>
                        <div class="chain-container">
                            ${chainSummary}
                        </div>
                    </div>
                ` : ''}

                <div class="section" style="page-break-before: auto;">
                    <div class="section-title">Unified Career Milestones Timeline</div>
                    <div class="table-container">
                        <table class="table">
                            <thead>
                                <tr>
                                    <th>Emp ID</th>
                                    <th>Seq</th>
                                    <th>Effective Date</th>
                                    <th>Job Role / Title</th>
                                    <th>Org Node</th>
                                    <th>Branch</th>
                                    <th>Reason</th>
                                    ${isSalaryVisible ? '<th class="text-right">Basic Salary</th>' : ''}
                                </tr>
                            </thead>
                            <tbody>
                                ${timelineRows || `<tr><td colspan="${isSalaryVisible ? 8 : 7}" style="text-align:center; padding:15px; color:#64748b;">No career milestones recorded.</td></tr>`}
                            </tbody>
                        </table>
                    </div>
                </div>
            </body>
            </html>
        `;

        printWindow.document.write(printHtml);
        printWindow.document.close();
    };

    const handleDeleteDoc = async (docId) => {
        if (!window.confirm("Remove this document?")) return;
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL1}/personnel/documents/${docId}`, { withCredentials: true, headers: { "X-Tenant": tenant } });
            fetchData();
        } catch (err) { console.error(err); }
    };

    const handleDeleteHistory = async (hid) => {
        if (!window.confirm("Permanently delete this career record?")) return;
        try {
            await axios.delete(`${process.env.REACT_APP_STAFF_API_URL}/staff/history/${hid}`, { withCredentials: true, headers: { "X-Tenant": tenant } });
            fetchData();
        } catch (err) { console.error(err); }
    };

    // Family CRUD Handlers
    const handleOpenFamilyDialog = (rec = null) => {
        if (rec) {
            setEditingFamily(rec.id);
            setFamilyForm({
                member_name: rec.member_name || '',
                relationship: rec.relationship || '',
                dob: rec.dob ? toInputDate(rec.dob) : '',
                salutation: rec.salutation || '',
                gender: rec.gender || '',
                marital_status: rec.marital_status || '',
                is_working: rec.is_working === 1,
                is_studying: rec.is_studying === 1,
                notes: rec.notes || ''
            });
        } else {
            setEditingFamily(null);
            setFamilyForm({
                member_name: '',
                relationship: '',
                dob: '',
                salutation: '',
                gender: '',
                marital_status: '',
                is_working: false,
                is_studying: false,
                notes: ''
            });
        }
        setFamilyDialogOpen(true);
    };

    const handleSaveFamily = async () => {
        if (!familyForm.member_name || !familyForm.relationship) {
            alert("Member name and relationship are required.");
            return;
        }

        try {
            const h = { "X-Tenant": tenant };
            const payload = {
                ...familyForm,
                dob: familyForm.dob || null,
                notes: familyForm.notes || null
            };

            if (editingFamily) {
                await axios.put(
                    `${process.env.REACT_APP_STAFF_API_URL}/staff/${id}/family/${editingFamily}`,
                    payload,
                    { withCredentials: true, headers: h }
                );
            } else {
                await axios.post(
                    `${process.env.REACT_APP_STAFF_API_URL}/staff/${id}/family`,
                    payload,
                    { withCredentials: true, headers: h }
                );
            }
            setFamilyDialogOpen(false);
            fetchData();
        } catch (err) {
            console.error("handleSaveFamily error:", err);
            alert("Failed to save family member: " + (err.response?.data?.message || err.message));
        }
    };

    const handleDeleteFamily = async (familyId) => {
        if (!window.confirm("Are you sure you want to delete this family member record?")) return;
        try {
            const h = { "X-Tenant": tenant };
            await axios.delete(
                `${process.env.REACT_APP_STAFF_API_URL}/staff/${id}/family/${familyId}`,
                { withCredentials: true, headers: h }
            );
            fetchData();
        } catch (err) {
            console.error("handleDeleteFamily error:", err);
            alert("Failed to delete family member: " + (err.response?.data?.message || err.message));
        }
    };

    const handleSaveHistory = async () => {
        if (!selectedHistory) return;
        try {
            await axios.put(`${process.env.REACT_APP_STAFF_API_URL}/staff/history/${selectedHistory.id}`, selectedHistory, { withCredentials: true, headers: { "X-Tenant": tenant } });
            setOpenHistoryEdit(false);
            fetchData();
        } catch (err) { console.error(err); }
    };

    const handleUploadDoc = async () => {
        if (!newDoc.file) return;
        setUploading(true);
        let fileToUpload = newDoc.file;
        try {
            if (fileToUpload.type.startsWith('image/')) {
                const options = { maxSizeMB: 2, maxWidthOrHeight: 1920, useWebWorker: true };
                fileToUpload = await imageCompression(fileToUpload, options);
            }
        } catch (err) {
            console.error('Compression error:', err);
        }

        const fd = new FormData();
        fd.append('document_type', newDoc.type);
        fd.append('notes', newDoc.notes);
        fd.append('file', fileToUpload);
        if (newDoc.document_no) fd.append('document_no', newDoc.document_no);
        if (newDoc.start_date) fd.append('start_date', newDoc.start_date);
        if (newDoc.expiry_date) fd.append('expiry_date', newDoc.expiry_date);
        if (newDoc.backFile) fd.append('back_file', newDoc.backFile);

        try {
            await axios.post(`${process.env.REACT_APP_API_URL1}/personnel/staff/${id}/documents`, fd, {
                withCredentials: true, headers: { "X-Tenant": tenant, 'Content-Type': 'multipart/form-data' }
            });
            setUploadDialogOpen(false);
            setNewDoc({ type: 'NRC', notes: '', expiry_date: '', document_no: '', start_date: '', file: null, backFile: null });
            fetchData();
        } catch (err) { console.error(err); } finally { setUploading(false); }
    };

    const handleSaveDisciplinary = async () => {
        if (!disciplinaryForm.discipline_id || !disciplinaryForm.severe_level_id || !disciplinaryForm.case_desc || !disciplinaryForm.case_date || !disciplinaryForm.dsoutcome_id) {
            alert("Please fill in all mandatory fields");
            return;
        }

        setUploading(true);
        const h = {
            "X-Tenant": tenant,
            "Content-Type": "multipart/form-data"
        };

        const fd = new FormData();
        fd.append("discipline_id", disciplinaryForm.discipline_id);
        fd.append("severe_level_id", disciplinaryForm.severe_level_id);
        fd.append("case_desc", disciplinaryForm.case_desc);
        fd.append("case_date", disciplinaryForm.case_date);
        if (disciplinaryForm.officer_id) fd.append("officer_id", disciplinaryForm.officer_id);
        fd.append("dsoutcome_id", disciplinaryForm.dsoutcome_id);
        if (disciplinaryForm.remark) fd.append("remark", disciplinaryForm.remark);

        let fileToUpload = disciplinaryForm.file;
        if (fileToUpload) {
            try {
                if (fileToUpload.type.startsWith('image/')) {
                    const options = { maxSizeMB: 2, maxWidthOrHeight: 1920, useWebWorker: true };
                    fileToUpload = await imageCompression(fileToUpload, options);
                }
            } catch (err) {
                console.error('Compression error:', err);
            }
            fd.append("file", fileToUpload);
        }

        try {
            if (editingDisciplinary) {
                await axios.put(
                    `${process.env.REACT_APP_STAFF_API_URL}/staff/${id}/discipline/${editingDisciplinary.id}`,
                    fd,
                    { withCredentials: true, headers: h }
                );
            } else {
                await axios.post(
                    `${process.env.REACT_APP_STAFF_API_URL}/staff/${id}/discipline`,
                    fd,
                    { withCredentials: true, headers: h }
                );
            }
            setDisciplinaryDialogOpen(false);
            setEditingDisciplinary(null);
            setDisciplinaryForm({
                discipline_id: '',
                severe_level_id: '',
                case_desc: '',
                case_date: '',
                officer_id: '',
                dsoutcome_id: '',
                remark: '',
                file: null
            });
            fetchData();
        } catch (err) {
            console.error(err);
            alert("Failed to save record: " + (err.response?.data?.message || err.message));
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteDisciplinary = async (disciplineId) => {
        if (!window.confirm("Are you sure you want to permanently delete this disciplinary record?")) return;
        try {
            await axios.delete(
                `${process.env.REACT_APP_STAFF_API_URL}/staff/${id}/discipline/${disciplineId}`,
                { withCredentials: true, headers: { "X-Tenant": tenant } }
            );
            fetchData();
        } catch (err) {
            console.error(err);
            alert("Failed to delete record: " + (err.response?.data?.message || err.message));
        }
    };

    const handleUploadDisciplineFile = async () => {
        if (!selectedDisciplineForAttachment || !disciplinaryForm.file) return;
        setUploading(true);
        const h = {
            "X-Tenant": tenant,
            "Content-Type": "multipart/form-data"
        };

        const formData = new FormData();
        const rec = selectedDisciplineForAttachment;
        formData.append("discipline_id", rec.discipline_id);
        formData.append("severe_level_id", rec.severe_level_id);
        formData.append("case_desc", rec.case_desc);
        formData.append("case_date", toInputDate(rec.case_date));
        if (rec.officer_id) formData.append("officer_id", rec.officer_id);
        formData.append("dsoutcome_id", rec.dsoutcome_id);
        if (rec.remark) formData.append("remark", rec.remark);
        formData.append("file", disciplinaryForm.file);

        try {
            const res = await axios.put(
                `${process.env.REACT_APP_STAFF_API_URL}/staff/${id}/discipline/${rec.id}`,
                formData,
                { withCredentials: true, headers: h }
            );
            if (res.data.success) {
                setUploadDisciplineAttachmentOpen(false);
                setDisciplinaryForm({ ...disciplinaryForm, file: null });
                fetchData();
            }
        } catch (err) {
            console.error(err);
            alert("File upload failed: " + (err.response?.data?.message || err.message));
        } finally {
            setUploading(false);
        }
    };

    if (!staff) return <Box sx={{ p: 4 }}><LinearProgress /></Box>;
    if (staff.error) return <Box sx={{ p: 4 }}><Typography color="error">Failed to load staff details. Please try again.</Typography></Box>;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />

            <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
                <Fade in timeout={600}>
                    <Box>
                        {/* Header Actions */}
                        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/staff')} sx={{ textTransform: 'none', fontWeight: 700, color: '#64748b' }}>Back to Directory</Button>
                        </Stack>

                        {/* Profile Hero Card */}
                        <Paper sx={{ p: 4, borderRadius: 5, mb: 3, position: 'relative', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)' }}>
                            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 6, background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.main} 0%, #a855f7 100%)` }} />

                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems="center">
                                <Box sx={{ position: 'relative' }}>
                                    <Avatar
                                        src={staff.photo_url}
                                        sx={{ width: 140, height: 140, border: '4px solid #fff', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
                                        imgProps={{ style: { objectPosition: 'center top' } }}
                                    >
                                        <PersonIcon sx={{ fontSize: 60 }} />
                                    </Avatar>
                                    <Box sx={{ position: 'absolute', bottom: 8, right: 8, width: 16, height: 16, bgcolor: staff.is_active ? '#10b981' : '#ef4444', border: '3px solid #fff', borderRadius: '50%' }} />
                                </Box>

                                <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
                                    <Typography variant="h3" sx={{ fontWeight: 900, color: '#0f172a', mb: 1, letterSpacing: '-0.02em' }}>
                                        {(staff.salutation ? `${staff.salutation} ` : '') + staff.full_name}
                                    </Typography>
                                    <Stack direction="row" spacing={1} justifyContent={{ xs: 'center', md: 'flex-start' }} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
                                        <Chip label={staff.staff_code} sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }} />
                                        <Chip label={staff.job_role_name || '—'} variant="outlined" sx={{ fontWeight: 700, borderColor: '#e2e8f0' }} />
                                        {/* Employment sequence badge for rehires */}
                                        {staff.employment_sequence > 1 && (
                                            <Chip
                                                label={`Rehire #${staff.employment_sequence}`}
                                                size="small"
                                                sx={{ fontWeight: 800, bgcolor: '#ede9fe', color: '#5b21b6', border: '1px solid #c4b5fd' }}
                                            />
                                        )}
                                        {/* Employment status (resigned/terminated) */}
                                        {(staff.employment_status === 'RESIGNED' || staff.employment_status === 'TERMINATED') ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#dc2626', textTransform: 'uppercase' }}>{staff.employment_status}</Typography>
                                            </Box>
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981' }} />
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#059669', textTransform: 'uppercase' }}>Active Member</Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-end' }, gap: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>Works as</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#334155' }}>{staff.job_role_name || '—'}</Typography>
                                </Box>
                            </Stack>

                            <Divider sx={{ my: 3, opacity: 0.6 }} />

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                                <InfoItem icon={HistoryIcon} label="Tenure" value={singleTenure.formatted} />
                                <InfoItem icon={GroupIcon} label="Reports To" value={staff.manager_name || '—'} />
                                <InfoItem icon={EventNoteIcon} label="Shift Pattern" value={staff.sm_pattern_name || '—'} />
                            </Box>

                            {(permissions.includes('MANAGE_STAFF') || ['super_admin', 'support_admin', 'hr_manager', 'hr', 'hr_branch'].includes(currentUserRole?.toLowerCase())) && (
                                <Stack
                                    direction="row"
                                    spacing={1.5}
                                    justifyContent={{ xs: 'center', md: 'flex-end' }}
                                    sx={{ mt: 3 }}
                                >
                                    {/* Rehire button — only for resigned/terminated staff */}
                                    {(staff.employment_status === 'RESIGNED' || staff.employment_status === 'TERMINATED') && (
                                        <Button
                                            variant="outlined"
                                            startIcon={<RehireIcon />}
                                            onClick={() => navigate('/staff/rehire', { state: { prefillStaffId: id } })}
                                            sx={{
                                                borderColor: 'primary.main', color: 'primary.main', fontWeight: 700,
                                                textTransform: 'none', borderRadius: 3, px: 3, py: 1,
                                                '&:hover': { bgcolor: '#ede9fe', borderColor: 'primary.main' }
                                            }}
                                        >
                                            Rehire
                                        </Button>
                                    )}
                                    {/* Offboard button — only for active/non-resigned staff */}
                                    {staff.employment_status !== 'RESIGNED' && staff.employment_status !== 'TERMINATED' && (
                                        <Button
                                            variant="outlined"
                                            startIcon={<OffboardIcon />}
                                            onClick={() => { setOffboardError(''); setOffboardOpen(true); }}
                                            sx={{
                                                borderColor: '#f87171', color: '#ef4444', fontWeight: 700,
                                                textTransform: 'none', borderRadius: 3, px: 3, py: 1,
                                                '&:hover': { bgcolor: '#fef2f2', borderColor: '#ef4444' }
                                            }}
                                        >
                                            Offboard
                                        </Button>
                                    )}
                                    <Button
                                        variant="outlined"
                                        startIcon={<DescriptionIcon />}
                                        onClick={() => setOpenReportDialog(true)}
                                        sx={{
                                            borderColor: 'primary.main', color: 'primary.main', fontWeight: 700,
                                            textTransform: 'none', borderRadius: 3, px: 3, py: 1,
                                            '&:hover': { bgcolor: '#ede9fe', borderColor: 'primary.main' }
                                        }}
                                    >
                                        Full Profile Report
                                    </Button>
                                    <Button
                                        variant="contained"
                                        startIcon={<EditIcon />}
                                        onClick={() => navigate(`/staff/edit/${id}`)}
                                        sx={{
                                            bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' },
                                            borderRadius: 3, px: 3, py: 1, fontWeight: 700, textTransform: 'none',
                                            boxShadow: (theme) => `0 10px 15px -3px ${theme.palette.primary.main}4d`
                                        }}
                                    >
                                        Edit Profile
                                    </Button>
                                </Stack>
                            )}
                        </Paper>

                        {/* Navigation Tabs */}
                        <Paper sx={{ borderRadius: 4, mb: 3, overflow: 'hidden', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)' }}>
                            <Tabs
                                value={tabValue}
                                onChange={(e, v) => setTabValue(v)}
                                sx={{
                                    px: 3, borderBottom: '1px solid #f1f5f9', bgcolor: '#fcfcfc',
                                    '& .MuiTab-root': { py: 2.5, minHeight: 0, textTransform: 'none', fontWeight: 700, fontSize: '0.9rem', color: '#64748b' },
                                    '& .Mui-selected': { color: 'primary.main' },
                                    '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', bgcolor: 'primary.main' }
                                }}
                            >
                                <Tab icon={<PersonIcon />} iconPosition="start" label="Personal" />
                                <Tab icon={<BusinessIcon />} iconPosition="start" label="Assignment" />
                                <Tab icon={<SecurityIcon />} iconPosition="start" label="Access & Policy" />
                                <Tab icon={<EventNoteIcon />} iconPosition="start" label="Schedule" />
                                {isPayrollAllowed && <Tab icon={<AssessmentIcon />} iconPosition="start" label="Payroll" />}
                                <Tab icon={<AssignmentIndIcon />} iconPosition="start" label="Vault" />
                                <Tab icon={<HistoryIcon />} iconPosition="start" label="Disciplinary" />
                            </Tabs>

                            <Box sx={{ p: 4, bgcolor: '#fff' }}>
                                {tabValue === 0 && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

                                        {/* ── Pending Approver Handover Alert Banner ── */}
                                        {pendingHandover.length > 0 && (
                                            <Box sx={{
                                                p: 2, borderRadius: 2,
                                                bgcolor: '#fff7ed', border: '1.5px solid #f97316',
                                                display: 'flex', alignItems: 'center',
                                                justifyContent: 'space-between', gap: 2,
                                                mt: -2
                                            }}>
                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                    <Box sx={{ fontSize: 22 }}>⚠️</Box>
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 800, color: '#c2410c', fontSize: 14 }}>
                                                            Pending Approver Reassignment
                                                        </Typography>
                                                        <Typography sx={{ color: '#92400e', fontSize: 13 }}>
                                                            This staff is still set as approver for <strong>{pendingHandover.length}</strong> employee(s). Please reassign before their last working day.
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={async () => {
                                                        const initAssign = {};
                                                        pendingHandover.forEach(a => { initAssign[a.staff_id] = ''; });
                                                        setHandoverList(pendingHandover);
                                                        setHandoverAssignments(initAssign);
                                                        setHandoverStep(true);
                                                        setOffboardOpen(true);
                                                        if (allStaffList.length === 0) {
                                                            try {
                                                                const res = await axios.get(
                                                                    `${process.env.REACT_APP_STAFF_API_URL}/staff/approver-candidates`,
                                                                    { withCredentials: true, headers: { "X-Tenant": tenant } }
                                                                );
                                                                setAllStaffList(res.data?.data || []);
                                                            } catch { /* non-critical */ }
                                                        }
                                                    }}
                                                    sx={{
                                                        bgcolor: '#f97316', '&:hover': { bgcolor: '#ea6c0a' },
                                                        borderRadius: 2, fontWeight: 800, whiteSpace: 'nowrap',
                                                        textTransform: 'none', px: 2.5, flexShrink: 0
                                                    }}
                                                >
                                                    Reassign Approvers Now
                                                </Button>
                                            </Box>
                                        )}

                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 2.5, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Identity & Demographics</Typography>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                                                <Box><InfoItem icon={PersonIcon} label="Gender" value={staff.gender ? staff.gender.charAt(0).toUpperCase() + staff.gender.slice(1).toLowerCase() : '-'} /></Box>
                                                <Box><InfoItem icon={EventNoteIcon} label="Date of Birth" value={formatDDMMYYYY(staff.date_of_birth)} /></Box>
                                                <Box><InfoItem icon={VerifiedUserIcon} label="Father's Name" value={staff.father_name || 'N/A'} /></Box>
                                                <Box>
                                                    <InfoItem
                                                        icon={VerifiedUserIcon}
                                                        label="NRC / ID"
                                                        value={
                                                            staff.nrc_number_english && staff.nrc_number_myanmar && staff.nrc_number_english !== staff.nrc_number_myanmar ? (
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>{staff.nrc_number_english}</Typography>
                                                                    <Typography variant="caption" sx={{ color: 'primary.dark', fontWeight: 800 }}>{staff.nrc_number_myanmar}</Typography>
                                                                </Box>
                                                            ) : (
                                                                staff.nrc_number || 'N/A'
                                                            )
                                                        }
                                                    />
                                                </Box>
                                                <Box><InfoItem icon={VerifiedUserIcon} label="Passport" value={staff.passport_number || 'N/A'} /></Box>
                                                <Box><InfoItem icon={VerifiedUserIcon} label="Social Sec No" value={staff.social_security_no || 'N/A'} /></Box>
                                                <Box><InfoItem icon={VerifiedUserIcon} label="Nationality" value={staff.nationality || 'Burmese'} /></Box>
                                                <Box><InfoItem icon={VerifiedUserIcon} label="Religion" value={staff.religion || 'Buddhist'} /></Box>
                                                <Box><InfoItem icon={VerifiedUserIcon} label="Race" value={staff.race || 'N/A'} /></Box>
                                                <Box><InfoItem icon={VerifiedUserIcon} label="Marital Status" value={staff.marital_status ? staff.marital_status.charAt(0).toUpperCase() + staff.marital_status.slice(1).toLowerCase() : '-'} /></Box>
                                                <Box><InfoItem icon={VerifiedUserIcon} label="Blood Type" value={staff.blood_type || 'N/A'} /></Box>
                                            </Box>

                                            <Divider sx={{ my: 4, borderStyle: 'dashed' }} />

                                            <Typography variant="subtitle2" sx={{ mb: 2.5, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contact Details</Typography>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                                                <Box sx={{ gridColumn: 'span 2' }}><InfoItem icon={LocationOnIcon} label="Permanent Address" value={staff.permanent_address || 'N/A'} /></Box>
                                                <Box><InfoItem icon={LocationOnIcon} label="City / Township" value={staff.city_township || 'N/A'} /></Box>
                                                <Box><InfoItem icon={LocationOnIcon} label="Country" value={staff.country || 'N/A'} /></Box>
                                                <Box><InfoItem icon={LocationOnIcon} label="Postcode" value={staff.postcode || 'N/A'} /></Box>
                                                <Box><InfoItem icon={EmailIcon} label="Personal Phone" value={staff.personal_phone || 'N/A'} /></Box>
                                                <Box sx={{ gridColumn: 'span 2' }}><InfoItem icon={LocationOnIcon} label="Correspondence Address" value={staff.correspondence_address || 'N/A'} /></Box>
                                                <Box><InfoItem icon={PersonIcon} label="Emergency Contact Name" value={staff.emergency_contact_name || 'N/A'} /></Box>
                                                <Box><InfoItem icon={EmailIcon} label="Emergency Contact Phone" value={staff.emergency_contact_phone || 'N/A'} /></Box>
                                                <Box sx={{ gridColumn: 'span 2' }}><InfoItem icon={EventNoteIcon} label="Remarks" value={staff.remarks || 'N/A'} /></Box>
                                            </Box>

                                            <Divider sx={{ my: 4, borderStyle: 'dashed' }} />

                                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                                                <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0', height: '100%' }}>
                                                    <Typography variant="subtitle2" sx={{ mb: 2.5, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tax Relief Profile</Typography>
                                                    <Stack spacing={2}>
                                                        <InfoItem icon={SecurityIcon} label="Spouse Relief" value={staff.relief_spouse === 1 ? 'Applicable' : 'Not Applicable'} />
                                                        <InfoItem icon={GroupIcon} label="Children Count" value={staff.relief_children_count || 0} />
                                                        <InfoItem icon={PersonIcon} label="Parents Count" value={staff.relief_parents_count || 0} />
                                                    </Stack>
                                                </Box>
                                                <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0', height: '100%' }}>
                                                    <Typography variant="subtitle2" sx={{ mb: 2.5, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Bank Information</Typography>
                                                    <InfoItem icon={BankIcon} label="Primary Account" value={staff.bank_account_no ? `${staff.bank_account_no} (${staff.bank_name})` : 'Not Provided'} />
                                                </Box>
                                            </Box>

                                            <Divider sx={{ my: 4, borderStyle: 'dashed' }} />

                                            <Box sx={{ mb: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Family Members</Typography>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<AddIcon />}
                                                    onClick={() => handleOpenFamilyDialog(null)}
                                                    sx={{
                                                        borderRadius: 2.5, fontWeight: 700, textTransform: 'none', color: 'primary.main', borderColor: 'primary.main',
                                                        '&:hover': { borderColor: 'primary.dark', bgcolor: '#f5f5ff' }
                                                    }}
                                                >
                                                    Add Family Member
                                                </Button>
                                            </Box>

                                            <Paper sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                                                <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.2fr 1.5fr 1.8fr 3fr 1.2fr', p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                                    <Box><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>NAME</Typography></Box>
                                                    <Box><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>RELATIONSHIP</Typography></Box>
                                                    <Box><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>GENDER</Typography></Box>
                                                    <Box><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>D.O.B</Typography></Box>
                                                    <Box><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>STATUSES</Typography></Box>
                                                    <Box><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>NOTES / REMARKS</Typography></Box>
                                                    <Box sx={{ textAlign: 'right' }}><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>ACTIONS</Typography></Box>
                                                </Box>

                                                {familyMembers.length === 0 ? (
                                                    <Box sx={{ p: 4, textAlign: 'center' }}>
                                                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>No family members recorded.</Typography>
                                                    </Box>
                                                ) : (
                                                    familyMembers.map((member) => (
                                                        <Box key={member.id} sx={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.2fr 1.5fr 1.8fr 3fr 1.2fr', p: 2, alignItems: 'center', borderBottom: '1px solid #f1f5f9', '&:last-child': { borderBottom: 'none' }, '&:hover': { bgcolor: '#fcfcfc' } }}>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                                                    {member.salutation ? `${member.salutation} ` : ''}{member.member_name}
                                                                </Typography>
                                                            </Box>
                                                            <Box>
                                                                <Chip label={member.relationship} size="small" sx={{ fontWeight: 700, bgcolor: '#e0f2fe', color: '#0369a1' }} />
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ color: '#475569' }}>
                                                                    {member.gender ? member.gender.charAt(0).toUpperCase() + member.gender.slice(1).toLowerCase() : '-'}
                                                                </Typography>
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ color: '#475569' }}>
                                                                    {member.dob ? formatDDMMYYYY(member.dob) : '-'}
                                                                </Typography>
                                                            </Box>
                                                            <Box>
                                                                <Stack direction="row" spacing={0.5}>
                                                                    {member.is_working === 1 && <Chip label="Working" size="small" variant="outlined" color="success" sx={{ fontSize: 9, height: 18, fontWeight: 700 }} />}
                                                                    {member.is_studying === 1 && <Chip label="Studying" size="small" variant="outlined" color="primary" sx={{ fontSize: 9, height: 18, fontWeight: 700 }} />}
                                                                    {member.is_working !== 1 && member.is_studying !== 1 && <Typography variant="caption" sx={{ color: '#94a3b8' }}>-</Typography>}
                                                                </Stack>
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                    {member.notes || '-'}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                                                <IconButton size="small" onClick={() => handleOpenFamilyDialog(member)} sx={{ color: '#64748b' }}>
                                                                    <EditIcon sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                                <IconButton size="small" onClick={() => handleDeleteFamily(member.id)} sx={{ color: '#ef4444' }}>
                                                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </Box>
                                                        </Box>
                                                    ))
                                                )}
                                            </Paper>
                                        </Box>
                                    </Box>
                                )}

                                {tabValue === 1 && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 2.5, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Organizational Alignment</Typography>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3 }}>
                                                {(() => {
                                                    if (somConfig && somConfig.active_levels_json && orgNodes.length > 0 && staff.org_node_id) {
                                                        const nodesMap = {};
                                                        orgNodes.forEach(n => nodesMap[n.id] = n);
                                                        const path = [];
                                                        let curr = nodesMap[staff.org_node_id];
                                                        while (curr) { path.unshift(curr); curr = nodesMap[curr.parent_id]; }
                                                        return somConfig.active_levels_json.slice(1).map(lv => {
                                                            const type = orgTypes.find(t => t.type_name.toLowerCase() === lv.toLowerCase());
                                                            const m = type && path.find(n => String(n.type_id) === String(type.id));
                                                            let displayVal = m ? m.node_name : '-';
                                                            if (displayVal === '-' && lv.toLowerCase().includes("department") && staff.department) {
                                                                displayVal = staff.department;
                                                            }
                                                            return <Box key={lv}><InfoItem icon={BusinessIcon} label={lv} value={displayVal} /></Box>;
                                                        });
                                                    }
                                                    return <Box><InfoItem icon={BusinessIcon} label="Dept/Unit" value={staff.org_node_name || 'Not assigned'} /></Box>;
                                                })()}
                                                <Box><InfoItem icon={LocationOnIcon} label="Official Branch" value={staff.branch_name || 'Global'} /></Box>
                                                {staff.department && (!somConfig || !somConfig.active_levels_json?.slice(1).some(lv => lv.toLowerCase().includes("department"))) && (
                                                    <Box><InfoItem icon={BusinessIcon} label="Department" value={staff.department} /></Box>
                                                )}
                                            </Box>

                                            <Divider sx={{ my: 4, borderStyle: 'dashed' }} />

                                            <Typography variant="subtitle2" sx={{ mb: 2.5, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Position & Tenure</Typography>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3 }}>
                                                <Box><InfoItem icon={BadgeIcon} label="Employee ID" value={staff.employee_number || staff.staff_code} /></Box>
                                                <Box><InfoItem icon={CategoryIcon} label="Job Family" value={staff.job_name} /></Box>
                                                <Box><InfoItem icon={WorkIcon} label="Job Title" value={staff.job_role_name} /></Box>
                                                <Box><InfoItem icon={BadgeIcon} label="Grade Level" value={staff.grade_name} /></Box>
                                                <Box><InfoItem icon={EventNoteIcon} label="Joining Date" value={formatDDMMYYYY(staff.joining_date)} /></Box>
                                                <Box><InfoItem icon={EventNoteIcon} label="Confirmation Date" value={formatDDMMYYYY(staff.confirmation_date) || 'Pending'} /></Box>
                                                <Box><InfoItem icon={EventNoteIcon} label="Employment Status" value={staff.employment_status || 'Regular'} /></Box>
                                                {staff.cessation_date && <Box><InfoItem icon={EventNoteIcon} label="Cessation Date" value={formatDDMMYYYY(staff.cessation_date)} /></Box>}
                                            </Box>
                                        </Box>
                                        <Box>
                                            <Box sx={{ p: 4, bgcolor: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0' }}>
                                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }} flexWrap="wrap">
                                                    <HistoryIcon sx={{ color: '#64748b' }} />
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', flexGrow: 1 }}>Career Timeline</Typography>

                                                    {(staff.employment_sequence > 1 || employmentChain.length > 1) && (
                                                        <Chip
                                                            label={`Employment #${staff.employment_sequence || 1}`}
                                                            size="small"
                                                            sx={{ fontWeight: 800, bgcolor: '#ede9fe', color: '#5b21b6', height: 20 }}
                                                        />
                                                    )}
                                                </Stack>
                                                <Stack spacing={3} sx={{ mt: 2 }}>
                                                    {careerTimeline.map((h, i) => (
                                                        <Box key={h.id} sx={{ position: 'relative', pl: 5 }}>
                                                            <Box sx={{
                                                                position: 'absolute', left: 8, top: 24, bottom: -24, width: 2,
                                                                background: i === careerTimeline.length - 1 ? 'transparent' : (theme) => `linear-gradient(to bottom, ${theme.palette.primary.main}, #e2e8f0)`
                                                            }} />
                                                            <Box sx={{
                                                                position: 'absolute', left: 0, top: 0, width: 18, height: 18, borderRadius: '50%',
                                                                bgcolor: i === 0 ? 'primary.main' : '#fff', border: '3px solid', borderColor: i === 0 ? '#e0e7ff' : '#cbd5e1',
                                                                boxShadow: i === 0 ? (theme) => `0 0 0 4px ${theme.palette.primary.main}1a` : 'none',
                                                                zIndex: 1
                                                            }} />
                                                            <Paper sx={{
                                                                p: 2.5, borderRadius: 3, border: '1px solid',
                                                                borderColor: i === 0 ? '#e0e7ff' : '#f1f5f9',
                                                                bgcolor: i === 0 ? '#f5f7ff' : '#fff',
                                                                boxShadow: i === 0 ? (theme) => `0 4px 12px ${theme.palette.primary.main}0d` : 'none'
                                                            }}>
                                                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                                                    <Box>
                                                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                                                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: i === 0 ? 'primary.main' : '#334155' }}>
                                                                                {h.event_type === 'COMPENSATION'
                                                                                    ? (h.assignment_reason === 'INCREMENT' ? 'Salary Increment' :
                                                                                        h.assignment_reason === 'DECREMENT' ? 'Salary Decrease' :
                                                                                            h.assignment_reason === 'INITIAL_SETUP' ? 'Starting Salary' :
                                                                                                h.assignment_reason === 'CORRECTION' ? 'Salary Correction' :
                                                                                                    'Salary Change')
                                                                                    : (h.job_role_name || h.role_name)}
                                                                            </Typography>
                                                                            <Chip
                                                                                label={h.assignment_reason || (i === assignmentHistory.length - 1 ? 'ONBOARDING' : 'UPDATE')}
                                                                                size="small"
                                                                                sx={{
                                                                                    height: 20, fontSize: 10, fontWeight: 800,
                                                                                    bgcolor: h.assignment_reason === 'PROMOTION' ? '#10b981' :
                                                                                        h.assignment_reason === 'TRANSFER' ? 'primary.main' :
                                                                                            h.assignment_reason === 'INCREMENT' ? '#f59e0b' :
                                                                                                h.assignment_reason === 'DECREMENT' ? '#8b5cf6' :
                                                                                                    ['RESIGNATION', 'TERMINATION', 'CESSATION'].includes(h.assignment_reason) ? '#ef4444' : '#f1f5f9',
                                                                                    color: h.assignment_reason === 'PROMOTION' ||
                                                                                        h.assignment_reason === 'TRANSFER' ||
                                                                                        h.assignment_reason === 'INCREMENT' ||
                                                                                        h.assignment_reason === 'DECREMENT' ||
                                                                                        ['RESIGNATION', 'TERMINATION', 'CESSATION'].includes(h.assignment_reason) ? '#fff' : '#64748b'
                                                                                }}
                                                                            />
                                                                        </Stack>
                                                                        {h.event_type === 'COMPENSATION' ? (
                                                                            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                                                                                {h.previous_salary !== null ? `${Number(h.previous_salary).toLocaleString()} → ` : ''}{Number(h.basic_salary).toLocaleString()} MMK
                                                                            </Typography>
                                                                        ) : (
                                                                            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>{h.org_node_name} • {h.branch_name}</Typography>
                                                                        )}

                                                                        {h.event_type !== 'COMPENSATION' && h.compensation_event && (
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                                                                <BankIcon sx={{ fontSize: 14, color: '#64748b' }} />
                                                                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>
                                                                                    {h.compensation_event.previous_salary !== null
                                                                                        ? `${Number(h.compensation_event.previous_salary).toLocaleString()} → `
                                                                                        : ''}
                                                                                    {Number(h.compensation_event.basic_salary).toLocaleString()} MMK
                                                                                </Typography>
                                                                            </Box>
                                                                        )}

                                                                    </Box>
                                                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                                        {h.event_type === 'COMPENSATION'
                                                                            ? formatDDMMYYYY(h.effective_from)
                                                                            : `${formatDDMMYYYY(h.effective_from)} - ${h.effective_to ? formatDDMMYYYY(h.effective_to) : '-'}`}
                                                                    </Typography>
                                                                    {h.event_type !== 'COMPENSATION' && permissions.includes('MANAGE_STAFF') && (
                                                                        <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                                                                            <Tooltip title="Edit Record"><IconButton size="small" onClick={() => { setSelectedHistory(h); setOpenHistoryEdit(true); }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                                                                            <Tooltip title="Delete Record"><IconButton size="small" color="error" onClick={() => handleDeleteHistory(h.id)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                                                                        </Stack>
                                                                    )}
                                                                </Stack>
                                                            </Paper>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            </Box>

                                            {/* ─── Complete Employment History (Rehire Chain) ─── */}
                                            {employmentChain.length > 1 && (
                                                <Box sx={{ mt: 4 }}>
                                                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                                                        <Box sx={{ p: 0.8, bgcolor: '#ede9fe', borderRadius: 2, display: 'flex' }}>
                                                            <HistoryIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
                                                                Complete Employment History
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                                {employmentChain.length} employment periods across rehires
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                    <Stack spacing={0}>
                                                        {employmentChain.map((period, periodIdx) => {
                                                            const isCurrent = period.staff_id === Number(id);
                                                            const isLast = periodIdx === employmentChain.length - 1;
                                                            const statusColor = period.employment_status === 'ACTIVE' ? '#10b981'
                                                                : period.employment_status === 'RESIGNED' ? '#f59e0b'
                                                                    : period.employment_status === 'TERMINATED' ? '#ef4444' : 'primary.main';
                                                            const statusBg = period.employment_status === 'ACTIVE' ? '#f0fdf4'
                                                                : period.employment_status === 'RESIGNED' ? '#fffbeb'
                                                                    : period.employment_status === 'TERMINATED' ? '#fef2f2' : '#f5f3ff';
                                                            return (
                                                                <Box key={period.staff_id}>
                                                                    <Paper sx={{
                                                                        p: 3, borderRadius: 3,
                                                                        border: `2px solid ${isCurrent ? '#e0e7ff' : '#f1f5f9'}`,
                                                                        bgcolor: isCurrent ? '#fafbff' : '#fff',
                                                                        boxShadow: isCurrent ? (theme) => `0 4px 20px ${theme.palette.primary.main}33` : 'none',
                                                                        position: 'relative', overflow: 'hidden'
                                                                    }}>
                                                                        <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: isCurrent ? 'primary.main' : statusColor, borderRadius: '3px 0 0 3px' }} />
                                                                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ pl: 1 }}>
                                                                            <Box>
                                                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
                                                                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                                                                        Employment #{period.employment_sequence || periodIdx + 1}
                                                                                    </Typography>
                                                                                    {isCurrent && <Chip label="Current" size="small" sx={{ fontSize: 10, fontWeight: 800, height: 20, bgcolor: 'primary.main', color: '#fff' }} />}
                                                                                    <Chip label={period.employment_status} size="small" sx={{ fontSize: 10, fontWeight: 800, height: 20, bgcolor: statusBg, color: statusColor }} />
                                                                                </Stack>
                                                                                <Stack direction="row" spacing={2} flexWrap="wrap">
                                                                                    <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700 }}>ID: {period.employee_number || period.staff_code}</Typography>
                                                                                    {period.job_role_name && (
                                                                                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                                                                            {period.job_role_name}{period.branch_name ? ` • ${period.branch_name}` : ''}
                                                                                        </Typography>
                                                                                    )}
                                                                                </Stack>
                                                                            </Box>
                                                                            <Stack direction="row" spacing={3}>
                                                                                <Box sx={{ textAlign: 'right' }}>
                                                                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.6rem' }}>Joined</Typography>
                                                                                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#334155' }}>{period.joining_date ? formatDDMMYYYY(period.joining_date) : '—'}</Typography>
                                                                                </Box>
                                                                                {period.cessation_date && (
                                                                                    <Box sx={{ textAlign: 'right' }}>
                                                                                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.6rem' }}>{period.cessation_reason || 'Left'}</Typography>
                                                                                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#ef4444' }}>{formatDDMMYYYY(period.cessation_date)}</Typography>
                                                                                    </Box>
                                                                                )}
                                                                            </Stack>
                                                                        </Stack>
                                                                        {period.assignments?.length > 0 && (
                                                                            <Box sx={{ mt: 2.5, pl: 1, borderTop: '1px dashed #e2e8f0', pt: 2 }}>
                                                                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1.5 }}>Position History</Typography>
                                                                                <Stack spacing={1}>
                                                                                    {period.assignments.map((a, ai) => (
                                                                                        <Stack key={a.id} direction="row" spacing={2} alignItems="center">
                                                                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: ai === 0 ? 'primary.main' : '#cbd5e1', flexShrink: 0 }} />
                                                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#334155', flex: 1 }}>
                                                                                                {a.job_role_name || '—'}{a.branch_name ? ` • ${a.branch_name}` : ''}
                                                                                            </Typography>
                                                                                            <Chip
                                                                                                label={a.assignment_reason || 'ONBOARDING'}
                                                                                                size="small"
                                                                                                sx={{
                                                                                                    fontSize: 9, fontWeight: 800, height: 18,
                                                                                                    bgcolor: a.assignment_reason === 'PROMOTION' ? '#dcfce7' : a.assignment_reason === 'TRANSFER' ? '#ede9fe' : a.assignment_reason === 'REHIRE' ? '#fef3c7' : '#f1f5f9',
                                                                                                    color: a.assignment_reason === 'PROMOTION' ? '#15803d' : a.assignment_reason === 'TRANSFER' ? '#6d28d9' : a.assignment_reason === 'REHIRE' ? '#92400e' : '#64748b',
                                                                                                }}
                                                                                            />
                                                                                            <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                                                                {formatDDMMYYYY(a.effective_from)} {a.effective_to ? `- ${formatDDMMYYYY(a.effective_to)}` : ''}
                                                                                            </Typography>
                                                                                        </Stack>
                                                                                    ))}
                                                                                </Stack>
                                                                            </Box>
                                                                        )}
                                                                    </Paper>
                                                                    {!isLast && (
                                                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                                                                            <Stack alignItems="center" spacing={0.5}>
                                                                                <Box sx={{ width: 2, height: 12, bgcolor: '#e2e8f0' }} />
                                                                                <Chip label="REHIRED" size="small" sx={{ fontSize: 9, fontWeight: 800, height: 18, bgcolor: '#ede9fe', color: '#6d28d9', border: '1px solid #c4b5fd' }} />
                                                                                <Box sx={{ width: 2, height: 12, bgcolor: '#e2e8f0' }} />
                                                                            </Stack>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            );
                                                        })}
                                                    </Stack>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                )}

                                {tabValue === 2 && (
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1.8fr' }, gap: 4 }}>
                                        {/* Policy & Approvals */}
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 2.5, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                OPERATIONAL POLICIES
                                            </Typography>
                                            <Stack spacing={0.5}>
                                                <InfoItem icon={WorkIcon} label="Work Modality" value={staff.work_type === 'shift_based' ? 'Shift System' : (staff.allow_split_shift ? 'Office Split Shift' : 'Standard Office')} />
                                                <InfoItem icon={SecurityIcon} label="OT Status" value={staff.ot_eligible ? 'Eligible' : 'Non-Eligible'} />
                                                {staff.ot_eligible && (
                                                    <>
                                                        <InfoItem icon={SecurityIcon} label="OT Policy" value={staff.ot_policy_code || '—'} />
                                                        <InfoItem icon={SecurityIcon} label="OT Rate Profile" value={staff.ot_rate_profile || '—'} />
                                                    </>
                                                )}
                                                <InfoItem icon={HistoryIcon} label="Attendance Mode" value={staff.auto_approve_manual_attendance ? 'Auto-Approve' : 'Approval Flow'} />
                                                <InfoItem icon={CameraAltIcon} label="Photo Requirement" value={staff.is_photo_required !== 0 ? 'Mandatory' : 'Optional'} />
                                                <InfoItem icon={SecurityIcon} label="Web Clock Access" value={staff.web_clock_allowed ? 'Allowed' : 'Mobile App Only'} />
                                                <InfoItem icon={SecurityIcon} label="Web Geo-Fence" value={staff.web_geofence_required ? 'Required' : 'Not Required'} />
                                                <InfoItem icon={AssessmentIcon} label="Payroll Status" value={staff.exclude_from_payroll ? 'Excluded from Calculations' : 'Active / Included'} />
                                                <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />
                                                <InfoItem icon={PersonIcon} label="Reporting Supervisor (L1)" value={staff.manager_name || '—'} />
                                                <InfoItem icon={PersonIcon} label="HR Approver (L2)" value={staff.hr_name || '—'} />
                                            </Stack>
                                        </Box>

                                        {/* Credentials & Access */}
                                        <Box>
                                            <Box sx={{ p: 4, bgcolor: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0', height: 'fit-content' }}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                        Credentials &amp; Access
                                                    </Typography>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<LockResetIcon />}
                                                        onClick={() => setOpenResetDialog(true)}
                                                        sx={{
                                                            textTransform: 'none',
                                                            borderRadius: 2,
                                                            borderColor: '#cbd5e1',
                                                            color: '#475569',
                                                            fontWeight: 700,
                                                            bgcolor: '#fff',
                                                            '&:hover': {
                                                                borderColor: '#94a3b8',
                                                                bgcolor: '#f1f5f9'
                                                            }
                                                        }}
                                                    >
                                                        Reset Sec
                                                    </Button>
                                                </Stack>
                                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                                                    <InfoItem icon={PersonIcon} label="System Username" value={staff.username || '—'} mb={0} />
                                                    <InfoItem icon={EmailIcon} label="Work Email" value={staff.email || '—'} mb={0} />
                                                    <InfoItem icon={VpnKeyIcon} label="System Rank" value={staff.role || '—'} mb={0} />
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                )}

                                {tabValue === 3 && (
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 2.5, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Duty Configuration</Typography>
                                            <Stack spacing={2}>
                                                <InfoItem icon={GroupIcon} label="Shift Group" value={staff.sm_group_name || '—'} />
                                                <InfoItem icon={EventNoteIcon} label="Active Pattern" value={staff.sm_pattern_name || '—'} />
                                                <InfoItem icon={SecurityIcon} label="Late Policy" value={latePolicies.find(p => p.id === staff.late_policy_id)?.policy_name || '—'} />
                                            </Stack>
                                        </Box>
                                        <Box sx={{ gridColumn: 'span 2' }}>
                                            <Typography variant="subtitle2" sx={{ mb: 2.5, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Geo-Access Scope</Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {staff.allowed_locations?.map(l => <Chip key={l} label={l} size="small" sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', fontWeight: 600, color: '#475569' }} icon={<LocationOnIcon sx={{ fontSize: 14 }} />} />)}
                                                {(!staff.allowed_locations || staff.allowed_locations.length === 0) && <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#94a3b8' }}>Unrestricted Access</Typography>}
                                            </Box>
                                        </Box>
                                    </Box>
                                )}

                                {/* Dynamic Tabs based on Payroll Permission */}
                                {isPayrollAllowed && tabValue === 4 && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 2.5, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Payroll Configuration</Typography>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 4 }}>
                                                <Box>
                                                    <Box sx={{ p: 3, borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: '#f8fafc', height: '100%' }}>
                                                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 2 }}>Core Earnings</Typography>
                                                        <Stack spacing={2}>
                                                            <InfoItem icon={BankIcon} label="Basic Salary" value={payrollFormData.basic_salary ? `${Number(payrollFormData.basic_salary).toLocaleString()} MMK` : '0 MMK'} />
                                                            <InfoItem icon={AssessmentIcon} label="Calc Engine" value={calcBases.find(b => b.id === payrollFormData.calculation_basis_id)?.basis_name || '—'} />
                                                            <InfoItem icon={EventNoteIcon} label="Effective Date" value={formatDDMMYYYY(payrollFormData.effective_from)} />
                                                        </Stack>
                                                    </Box>
                                                </Box>
                                                <Box>
                                                    <Box sx={{ p: 3, borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: '#f8fafc', height: '100%' }}>
                                                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 2 }}>Recurring Allowances/Deducts</Typography>
                                                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                                            {payrollFormData.recurring_elements.map((el, i) => {
                                                                const metadata = payrollElements.find(e => e.id === el.element_id);
                                                                const status = getRecurringElementStatus(el);
                                                                const isExpired = status.code === 'EXPIRED';
                                                                return (
                                                                    <Box key={i}>
                                                                        <Paper sx={{
                                                                            p: 1.5,
                                                                            px: 2,
                                                                            borderRadius: 3,
                                                                            border: '1px solid',
                                                                            borderColor: isExpired ? '#e2e8f0' : '#f1f5f9',
                                                                            bgcolor: isExpired ? '#f8fafc' : '#fff',
                                                                            opacity: isExpired ? 0.72 : 1,
                                                                            display: 'flex',
                                                                            justifyContent: 'space-between',
                                                                            alignItems: 'center'
                                                                        }}>
                                                                            <Box>
                                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: isExpired ? '#64748b' : '#334155' }}>{metadata?.element_name}</Typography>
                                                                                    <Chip size="small" label={status.label} color={status.color} variant={isExpired ? 'outlined' : 'filled'} sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
                                                                                </Stack>
                                                                                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                                                                                    {metadata?.element_type} · {formatDDMMYYYY(el.effective_from)} – {el.effective_to ? formatDDMMYYYY(el.effective_to) : 'No expiry'}
                                                                                </Typography>
                                                                            </Box>
                                                                            <Typography variant="subtitle2" sx={{
                                                                                fontWeight: 800,
                                                                                color: isExpired ? '#94a3b8' : (metadata?.element_type === 'DEDUCTION' ? '#ef4444' : '#10b981'),
                                                                                textDecoration: isExpired ? 'line-through' : 'none'
                                                                            }}>
                                                                                {metadata?.element_type === 'DEDUCTION' ? '-' : '+'}{Number(el.amount).toLocaleString()}
                                                                            </Typography>
                                                                        </Paper>
                                                                    </Box>
                                                                );
                                                            })}
                                                            {payrollFormData.recurring_elements.length === 0 && (
                                                                <Box sx={{ gridColumn: 'span 2' }}>
                                                                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#94a3b8', py: 2, textAlign: 'center' }}>No recurring elements configured.</Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 2.5, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Statutory Calculations</Typography>
                                            <Box sx={{ p: 3, borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 3 }}>
                                                    <Box>
                                                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', display: 'block' }}>Calculate SSB</Typography>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Switch checked={Number(payrollFormData.ssb_enabled) === 1} disabled sx={{ '&.Mui-disabled': { opacity: 0.8 } }} />
                                                            <Typography variant="body2" fontWeight={700}>{payrollFormData.ssb_enabled === null ? 'Company Default' : Number(payrollFormData.ssb_enabled) === 1 ? 'Enabled' : 'Disabled'}</Typography>
                                                        </Stack>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', display: 'block' }}>Calculate Income Tax</Typography>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Switch checked={Number(payrollFormData.pit_enabled) === 1} disabled sx={{ '&.Mui-disabled': { opacity: 0.8 } }} />
                                                            <Typography variant="body2" fontWeight={700}>{payrollFormData.pit_enabled === null ? 'Company Default' : Number(payrollFormData.pit_enabled) === 1 ? 'Enabled' : 'Disabled'}</Typography>
                                                        </Stack>
                                                    </Box>
                                                    <InfoItem
                                                        icon={VerifiedUserIcon}
                                                        label="SSB Scheme"
                                                        value={{ INHERIT: 'Use Company Policy', STANDARD: 'Standard Scheme', OVER_60_REGISTERED: 'Over-60 Registered Scheme' }[payrollFormData.ssb_scheme_override] || 'Use Company Policy'}
                                                        mb={0}
                                                    />
                                                    <InfoItem icon={EventNoteIcon} label="Effective Date" value={formatDDMMYYYY(payrollFormData.statutory_effective_from)} mb={0} />
                                                </Box>
                                                {payrollFormData.statutory_reason && (
                                                    <Typography variant="body2" sx={{ color: '#64748b', mt: 2 }}><strong>Change reason:</strong> {payrollFormData.statutory_reason}</Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        <Box sx={{ mt: 4 }}>
                                            <Box sx={{ p: 3, borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cost Centre Allocations</Typography>
                                                    {permissions.includes('MANAGE_STAFF') && (
                                                        <Button size="small" variant="outlined" onClick={() => {
                                                            setCcAllocationsForm(costCentreAllocations.length > 0 ? [...costCentreAllocations] : [{ cost_centre_id: '', allocation_percentage: 100 }]);
                                                            setOpenCostCentreDialog(true);
                                                        }} sx={{ borderRadius: 2, textTransform: 'none', py: 0.5 }}>
                                                            Manage Allocations
                                                        </Button>
                                                    )}
                                                </Stack>
                                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                                                    {costCentreAllocations.length > 0 ? costCentreAllocations.map((alloc, i) => {
                                                        const cc = costCentres.find(c => String(c.id) === String(alloc.cost_centre_id));
                                                        return (
                                                            <Paper key={i} sx={{ p: 1.5, px: 2, borderRadius: 3, border: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Box>
                                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>{cc?.name || 'Unknown'}</Typography>
                                                                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>{cc?.code || '-'}</Typography>
                                                                </Box>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.main' }}>
                                                                    {Number(alloc.allocation_percentage)}%
                                                                </Typography>
                                                            </Paper>
                                                        );
                                                    }) : (
                                                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#94a3b8', py: 2 }}>No cost centre allocations configured. Defaults to main cost centre.</Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                )}

                                {((isPayrollAllowed && tabValue === 5) || (!isPayrollAllowed && tabValue === 4)) && (
                                    <Box>
                                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 3 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Verified Document Vault</Typography>
                                            <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setUploadDialogOpen(true)} sx={{ borderRadius: 2, textTransform: 'none' }}>Upload New</Button>
                                        </Stack>
                                        <Paper sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid #f1f5f9' }}>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: '4fr 3fr 2.5fr 2.5fr 2.5fr 3.5fr', p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                                                <Box><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>DOCUMENT</Typography></Box>
                                                <Box><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>DOCUMENT NO.</Typography></Box>
                                                <Box><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>START DATE</Typography></Box>
                                                <Box><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>EXPIRY</Typography></Box>
                                                <Box><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>UPLOADED</Typography></Box>
                                                <Box sx={{ textAlign: 'right' }}><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>ACTIONS</Typography></Box>
                                            </Box>
                                            {documents.map((d, i) => (
                                                <Box key={d.id}>
                                                    <Box sx={{ display: 'grid', gridTemplateColumns: '4fr 3fr 2.5fr 2.5fr 2.5fr 3.5fr', p: 2, alignItems: 'center', '&:hover': { bgcolor: '#fcfcfc' } }}>
                                                        <Box>
                                                            <Stack direction="row" spacing={2} alignItems="center">
                                                                <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6', width: 32, height: 32 }}><DescriptionIcon sx={{ fontSize: 18 }} /></Avatar>
                                                                <Box>
                                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>{d.document_type}</Typography>
                                                                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>{d.file_name || 'No file attached'}</Typography>
                                                                    {d.notes && <Typography variant="caption" display="block" sx={{ color: '#64748b', fontStyle: 'italic', mt: 0.5 }}>{d.notes}</Typography>}
                                                                </Box>
                                                            </Stack>
                                                        </Box>
                                                        <Box><Typography variant="body2" sx={{ color: '#64748b' }}>{d.document_no || '-'}</Typography></Box>
                                                        <Box><Typography variant="body2" sx={{ color: '#64748b' }}>{d.start_date ? formatDDMMYYYY(d.start_date) : '-'}</Typography></Box>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ color: d.expiry_date && new Date(d.expiry_date) < new Date() ? '#ef4444' : '#64748b', fontWeight: d.expiry_date ? 700 : 400 }}>
                                                                {d.expiry_date ? formatDDMMYYYY(d.expiry_date) : '-'}
                                                            </Typography>
                                                        </Box>
                                                        <Box><Typography variant="body2" sx={{ color: '#64748b' }}>{formatDDMMYYYY(d.created_at)}</Typography></Box>
                                                        <Box sx={{ textAlign: 'right' }}>
                                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                                                                {d.dms_id ? (
                                                                    <>
                                                                        <Tooltip title="Preview"><IconButton size="small" onClick={() => window.open(`${process.env.REACT_APP_API_URL1}/admin/documents/${d.dms_id}/preview?tenant=${tenant}`, '_blank')}><PreviewIcon fontSize="small" /></IconButton></Tooltip>
                                                                        <Tooltip title="Download"><IconButton size="small" onClick={() => window.location.href = `${process.env.REACT_APP_API_URL1}/admin/documents/${d.dms_id}/download?tenant=${tenant}`}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
                                                                    </>
                                                                ) : (
                                                                    <Tooltip title="No file attached - Imported metadata only"><Chip label="No File" size="small" color="warning" variant="outlined" sx={{ mr: 1, height: 24, fontSize: '0.75rem', fontWeight: 600 }} /></Tooltip>
                                                                )}
                                                                <Tooltip title="Remove"><IconButton size="small" color="error" onClick={() => handleDeleteDoc(d.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                                            </Stack>
                                                        </Box>
                                                    </Box>
                                                    <Divider />
                                                </Box>
                                            ))}
                                            {documents.length === 0 && (
                                                <Box sx={{ p: 8, textAlign: 'center' }}>
                                                    <Typography variant="body1" sx={{ color: '#94a3b8', mb: 1 }}>No documents uploaded yet.</Typography>
                                                    <Typography variant="caption" sx={{ color: '#cbd5e1' }}>Upload important employee documents to the secure vault.</Typography>
                                                </Box>
                                            )}
                                        </Paper>
                                    </Box>
                                )}

                                {tabValue === (isPayrollAllowed ? 6 : 5) && (
                                    <Box>
                                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 3 }} flexWrap="wrap" gap={2}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Disciplinary History
                                            </Typography>
                                            {permissions.includes('EDIT_STAFF') && (
                                                <Button
                                                    variant="contained"
                                                    startIcon={<AddIcon />}
                                                    onClick={() => {
                                                        setEditingDisciplinary(null);
                                                        setDisciplinaryForm({
                                                            discipline_id: '',
                                                            severe_level_id: '',
                                                            case_desc: '',
                                                            case_date: '',
                                                            officer_id: '',
                                                            dsoutcome_id: '',
                                                            remark: '',
                                                            file: null
                                                        });
                                                        setDisciplinaryDialogOpen(true);
                                                    }}
                                                    sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                                                >
                                                    Add Disciplinary Case
                                                </Button>
                                            )}
                                        </Stack>
                                        <Paper sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid #f1f5f9' }}>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr 1fr 1.5fr 1.5fr 3fr 2fr 2fr', p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                                                <Box><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>DATE</Typography></Box>
                                                <Box><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>CATEGORY</Typography></Box>
                                                <Box><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>SEVERITY</Typography></Box>
                                                <Box><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>OFFICER</Typography></Box>
                                                <Box><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>OUTCOME</Typography></Box>
                                                <Box><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>DESCRIPTION</Typography></Box>
                                                <Box><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>SUPPORTING DOC</Typography></Box>
                                                <Box sx={{ textAlign: 'right' }}><Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8' }}>ACTIONS</Typography></Box>
                                            </Box>
                                            {disciplineHistory.map((rec) => (
                                                <Box key={rec.id}>
                                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr 1fr 1.5fr 1.5fr 3fr 2fr 2fr', p: 2, alignItems: 'center', '&:hover': { bgcolor: '#fcfcfc' } }}>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                                                                {formatDDMMYYYY(rec.case_date)}
                                                            </Typography>
                                                        </Box>
                                                        <Box>
                                                            <Chip label={rec.discipline_code || 'Other'} size="small" sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#475569' }} />
                                                        </Box>
                                                        <Box>
                                                            <Chip
                                                                label={rec.level_code || `Level ${rec.severe_level_id}`}
                                                                size="small"
                                                                sx={{
                                                                    fontWeight: 800,
                                                                    bgcolor: rec.severe_level_id === 5 ? '#e0f2fe' :
                                                                             rec.severe_level_id === 6 ? '#dbeafe' :
                                                                             rec.severe_level_id === 7 ? '#fef9c3' :
                                                                             rec.severe_level_id === 8 ? '#ffedd5' :
                                                                             rec.severe_level_id === 9 ? '#fee2e2' : '#f87171',
                                                                    color: rec.severe_level_id === 5 ? '#0369a1' :
                                                                           rec.severe_level_id === 6 ? '#1d4ed8' :
                                                                           rec.severe_level_id === 7 ? '#a16207' :
                                                                           rec.severe_level_id === 8 ? '#c2410c' :
                                                                           rec.severe_level_id === 9 ? '#b91c1c' : '#ffffff',
                                                                }}
                                                            />
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600 }}>
                                                                {rec.officer_name || '-'}
                                                            </Typography>
                                                        </Box>
                                                        <Box>
                                                            <Chip
                                                                label={rec.outcome_code}
                                                                size="small"
                                                                sx={{
                                                                    fontWeight: 800,
                                                                    bgcolor: rec.outcome_code?.toLowerCase().includes('termination') ? '#fef2f2' :
                                                                             rec.outcome_code?.toLowerCase().includes('demotion') || rec.outcome_code?.toLowerCase().includes('suspension') ? '#fffbeb' : '#f0fdf4',
                                                                    color: rec.outcome_code?.toLowerCase().includes('termination') ? '#ef4444' :
                                                                           rec.outcome_code?.toLowerCase().includes('demotion') || rec.outcome_code?.toLowerCase().includes('suspension') ? '#d97706' : '#15803d',
                                                                }}
                                                            />
                                                        </Box>
                                                        <Box>
                                                            <Tooltip title={rec.case_desc}>
                                                                <Typography variant="body2" sx={{ color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                                                    {rec.case_desc}
                                                                </Typography>
                                                            </Tooltip>
                                                            {rec.remark && (
                                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                                                                    Note: {rec.remark}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Box>
                                                            {rec.dms_id ? (
                                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                                    <DescriptionIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                                                                    <Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>
                                                                        {rec.support_doc_name || 'Attached file'}
                                                                    </Typography>
                                                                </Stack>
                                                            ) : rec.support_doc_name ? (
                                                                <Box>
                                                                    <Typography variant="caption" sx={{ color: '#64748b', fontStyle: 'italic', display: 'block' }}>
                                                                        {rec.support_doc_name}
                                                                    </Typography>
                                                                    {permissions.includes('EDIT_STAFF') && (
                                                                        <Button
                                                                            size="small"
                                                                            startIcon={<UploadIcon sx={{ fontSize: 12 }} />}
                                                                            onClick={() => {
                                                                                setSelectedDisciplineForAttachment(rec);
                                                                                setDisciplinaryForm({ ...disciplinaryForm, file: null });
                                                                                setUploadDisciplineAttachmentOpen(true);
                                                                            }}
                                                                            sx={{ fontSize: '0.65rem', textTransform: 'none', py: 0 }}
                                                                        >
                                                                            Link file
                                                                        </Button>
                                                                    )}
                                                                </Box>
                                                            ) : (
                                                                permissions.includes('EDIT_STAFF') && (
                                                                    <Button
                                                                        size="small"
                                                                        startIcon={<UploadIcon sx={{ fontSize: 12 }} />}
                                                                        onClick={() => {
                                                                            setSelectedDisciplineForAttachment(rec);
                                                                            setDisciplinaryForm({ ...disciplinaryForm, file: null });
                                                                            setUploadDisciplineAttachmentOpen(true);
                                                                        }}
                                                                        sx={{ fontSize: '0.65rem', textTransform: 'none', color: '#64748b' }}
                                                                    >
                                                                        Upload Doc
                                                                    </Button>
                                                                )
                                                            )}
                                                        </Box>
                                                        <Box sx={{ textAlign: 'right' }}>
                                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                                {rec.dms_id && (
                                                                    <>
                                                                        <Tooltip title="Preview"><IconButton size="small" onClick={() => window.open(`${process.env.REACT_APP_API_URL1}/admin/documents/${rec.dms_id}/preview?tenant=${tenant}&token=${sessionStorage.getItem("token") || ""}`, '_blank')}><PreviewIcon fontSize="small" /></IconButton></Tooltip>
                                                                        <Tooltip title="Download"><IconButton size="small" onClick={() => window.location.href = `${process.env.REACT_APP_API_URL1}/admin/documents/${rec.dms_id}/download?tenant=${tenant}`}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
                                                                    </>
                                                                )}
                                                                {permissions.includes('EDIT_STAFF') && (
                                                                    <>
                                                                        <Tooltip title="Edit Record">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => {
                                                                                    setEditingDisciplinary(rec);
                                                                                    setDisciplinaryForm({
                                                                                        discipline_id: rec.discipline_id || '',
                                                                                        severe_level_id: rec.severe_level_id || '',
                                                                                        case_desc: rec.case_desc || '',
                                                                                        case_date: toInputDate(rec.case_date) || '',
                                                                                        officer_id: rec.officer_id || '',
                                                                                        dsoutcome_id: rec.dsoutcome_id || '',
                                                                                        remark: rec.remark || '',
                                                                                        file: null
                                                                                    });
                                                                                    setDisciplinaryDialogOpen(true);
                                                                                }}
                                                                            >
                                                                                <EditIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Tooltip title="Delete Record">
                                                                            <IconButton size="small" color="error" onClick={() => handleDeleteDisciplinary(rec.id)}>
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </>
                                                                )}
                                                            </Stack>
                                                        </Box>
                                                    </Box>
                                                    <Divider />
                                                </Box>
                                            ))}
                                            {disciplineHistory.length === 0 && (
                                                <Box sx={{ p: 8, textAlign: 'center' }}>
                                                    <Typography variant="body1" sx={{ color: '#94a3b8', mb: 1 }}>No disciplinary records.</Typography>
                                                    <Typography variant="caption" sx={{ color: '#cbd5e1' }}>This employee has a clean record.</Typography>
                                                </Box>
                                            )}
                                        </Paper>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Box>
                </Fade>
            </Box>

            {/* Reset Password Dialog */}
            <Dialog open={openResetDialog} onClose={() => setOpenResetDialog(false)} PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>Reset System Password</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 3, color: '#64748b' }}>Set a new secure password for this user's system account.</Typography>
                    <TextField
                        fullWidth
                        type={showPassword ? "text" : "password"}
                        label="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)}>{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}</IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenResetDialog(false)} sx={{ fontWeight: 700, color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleResetPassword} disabled={resetLoading || newPassword.length < 6} sx={{ borderRadius: 2, px: 4, fontWeight: 700 }}>
                        {resetLoading ? 'Saving...' : 'Update Password'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Document Upload Dialog */}
            <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 500, p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>Upload to Secure Vault</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            select
                            fullWidth
                            label="Document Type"
                            value={newDoc.type}
                            onChange={(e) => setNewDoc({ ...newDoc, type: e.target.value })}
                        >
                            <MenuItem value="NRC">National ID / NRC</MenuItem>
                            <MenuItem value="PASSPORT">Passport</MenuItem>
                            <MenuItem value="CV">Curriculum Vitae (CV)</MenuItem>
                            <MenuItem value="CERTIFICATE">Educational Certificate</MenuItem>
                            {canViewContracts && (
                                <MenuItem value="CONTRACT">Employment Contract</MenuItem>
                            )}
                            <MenuItem value="OFFER_LETTER">Offer Letter</MenuItem>
                            <MenuItem value="DRIVING_LICENSE">Driving License</MenuItem>
                            <MenuItem value="OTHER">Other Document</MenuItem>
                        </TextField>

                        <TextField
                            fullWidth
                            label="Notes / Description"
                            multiline
                            rows={2}
                            value={newDoc.notes}
                            onChange={(e) => setNewDoc({ ...newDoc, notes: e.target.value })}
                        />

                        <TextField
                            fullWidth
                            label="Document No."
                            value={newDoc.document_no || ''}
                            onChange={(e) => setNewDoc({ ...newDoc, document_no: e.target.value })}
                            helperText="Optional: Set this if the document has a reference number (e.g. Contract No, Passport No)."
                        />

                        <Stack direction="row" spacing={2}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Start Date"
                                InputLabelProps={{ shrink: true }}
                                value={newDoc.start_date || ''}
                                onChange={(e) => setNewDoc({ ...newDoc, start_date: e.target.value })}
                                sx={getTextFieldStyle()}
                                helperText="Optional: Document issue or start date."
                            />
                            <TextField
                                fullWidth
                                type="date"
                                label="Expiry Date"
                                InputLabelProps={{ shrink: true }}
                                value={newDoc.expiry_date || ''}
                                onChange={(e) => setNewDoc({ ...newDoc, expiry_date: e.target.value })}
                                sx={getTextFieldStyle()}
                                helperText="Optional: Document expiration date."
                            />
                        </Stack>

                        <Box sx={{ p: 3, border: '2px dashed #e2e8f0', borderRadius: 4, textAlign: 'center', bgcolor: '#f8fafc' }}>
                            <input
                                type="file"
                                id="vault-file"
                                hidden
                                onChange={(e) => setNewDoc({ ...newDoc, file: e.target.files[0] })}
                            />
                            <label htmlFor="vault-file">
                                <IconButton component="span" sx={{ bgcolor: '#fff', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', mb: 1 }}>
                                    <UploadIcon color="primary" />
                                </IconButton>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>
                                    {newDoc.file ? newDoc.file.name : 'Click to select file'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#94a3b8' }}>PDF, JPG, or PNG (Max 5MB)</Typography>
                            </label>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setUploadDialogOpen(false)} sx={{ fontWeight: 700, color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleUploadDoc} disabled={uploading || !newDoc.file} sx={{ borderRadius: 2, px: 4, fontWeight: 700 }}>
                        {uploading ? 'Uploading...' : 'Securely Upload'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={openHistoryEdit} onClose={() => setOpenHistoryEdit(false)} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 500, p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>Edit Career Record</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Effective From"
                            InputLabelProps={{ shrink: true }}
                            value={selectedHistory?.effective_from ? toInputDate(selectedHistory.effective_from) : ''}
                            onChange={(e) => setSelectedHistory({ ...selectedHistory, effective_from: e.target.value })}
                        />
                        <TextField
                            select
                            fullWidth
                            label="Assignment Reason"
                            value={selectedHistory?.assignment_reason || ''}
                            onChange={(e) => setSelectedHistory({ ...selectedHistory, assignment_reason: e.target.value })}
                        >
                            <MenuItem value="ONBOARDING">Onboarding</MenuItem>
                            <MenuItem value="CONFIRMATION">Confirmation</MenuItem>
                            <MenuItem value="PROMOTION">Promotion</MenuItem>
                            <MenuItem value="TRANSFER">Transfer</MenuItem>
                            <MenuItem value="REDESIGNATION">Redesignation</MenuItem>
                            <MenuItem value="ANNUAL_INCREMENT">Annual Increment</MenuItem>
                            <MenuItem value="INCREMENT">Increment</MenuItem>
                            <MenuItem value="DECREMENT">Decrement</MenuItem>
                            <MenuItem value="DEMOTION">Demotion</MenuItem>
                            <MenuItem value="CORRECTION">Correction / Update</MenuItem>
                            <MenuItem value="RESIGNATION">Resignation</MenuItem>
                            <MenuItem value="TERMINATION">Termination</MenuItem>
                            <MenuItem value="CESSATION">Cessation</MenuItem>
                        </TextField>
                        <TextField
                            select
                            fullWidth
                            label="Organization Node"
                            value={selectedHistory?.org_node_id || ''}
                            onChange={(e) => setSelectedHistory({ ...selectedHistory, org_node_id: e.target.value })}
                        >
                            <MenuItem value="">Unassigned</MenuItem>
                            {orgNodes.map(n => (
                                <MenuItem key={n.id} value={n.id} sx={{ pl: 2 + (n._depth || 0) * 2 }}>
                                    {n._depth > 0 && <span style={{ color: '#94a3b8', marginRight: 4 }}>{'└'}</span>}
                                    {n.node_name}{n.level_type ? ` (${n.level_type})` : ''}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            fullWidth
                            label="Job Role"
                            value={selectedHistory?.job_role_id || ''}
                            onChange={(e) => setSelectedHistory({ ...selectedHistory, job_role_id: e.target.value })}
                        >
                            <MenuItem value="">Unassigned</MenuItem>
                            {jobRoles.map(r => <MenuItem key={r.id} value={r.id}>{r.role_name}</MenuItem>)}
                        </TextField>
                        <TextField
                            select
                            fullWidth
                            label="Official Branch"
                            value={selectedHistory?.branch_id || ''}
                            onChange={(e) => setSelectedHistory({ ...selectedHistory, branch_id: e.target.value })}
                        >
                            <MenuItem value="">Global</MenuItem>
                            {orgBranches.map(b => <MenuItem key={b.id} value={b.id}>{b.branch_name}</MenuItem>)}
                        </TextField>
                        <TextField
                            select
                            fullWidth
                            label="Job Grade"
                            value={selectedHistory?.grade_id || ''}
                            onChange={(e) => setSelectedHistory({ ...selectedHistory, grade_id: e.target.value })}
                        >
                            <MenuItem value="">Unassigned</MenuItem>
                            {jobGrades.map(g => <MenuItem key={g.id} value={g.id}>{g.grade_name}</MenuItem>)}
                        </TextField>
                        <TextField
                            fullWidth
                            label="Notes"
                            multiline
                            rows={2}
                            value={selectedHistory?.assignment_notes || ''}
                            onChange={(e) => setSelectedHistory({ ...selectedHistory, assignment_notes: e.target.value })}
                        />
                        <TextField
                            fullWidth
                            label="Basic Salary"
                            type="number"
                            value={selectedHistory?.basic_salary || 0}
                            onChange={(e) => setSelectedHistory({ ...selectedHistory, basic_salary: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenHistoryEdit(false)} sx={{ fontWeight: 700, color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveHistory} sx={{ borderRadius: 2, px: 4, fontWeight: 700 }}>Save Changes</Button>
                </DialogActions>
            </Dialog>



            {/* ─── Complete Profile Report Preview Dialog ─── */}
            <Dialog
                open={openReportDialog}
                onClose={() => setOpenReportDialog(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3, pb: 2, borderBottom: '1px solid #f1f5f9' }}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>Complete Profile Report</Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                            {staff?.full_name} · {staff?.employee_number || staff?.staff_code}
                            {staff?.employment_sequence > 1 && ` · Rehire #${staff.employment_sequence}`}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="contained"
                            startIcon={<PrintIcon />}
                            onClick={handlePrintReport}
                            sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, borderRadius: 3, fontWeight: 700, textTransform: 'none' }}
                        >
                            Print / Export PDF
                        </Button>
                        <IconButton onClick={() => setOpenReportDialog(false)} sx={{ color: '#64748b' }}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </Box>

                <DialogContent sx={{ p: 3 }}>
                    {/* ── Summary Banner ── */}
                    <Box sx={{ p: 3, borderRadius: 3, bgcolor: '#f5f7ff', border: '1px solid #e0e7ff', mb: 3 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between">
                            <Box>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Employment Status</Typography>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                    <Chip
                                        label={staff?.display_status === 'NOTICE_PERIOD' ? 'Notice Period' : staff?.employment_status}
                                        size="small"
                                        sx={{
                                            fontWeight: 800,
                                            bgcolor: staff?.display_status === 'NOTICE_PERIOD' ? '#fff7ed'
                                                : staff?.employment_status === 'RESIGNED' ? '#fef2f2'
                                                : staff?.employment_status === 'TERMINATED' ? '#fef2f2' : '#dcfce7',
                                            color: staff?.display_status === 'NOTICE_PERIOD' ? '#c2410c'
                                                : staff?.employment_status === 'RESIGNED' ? '#dc2626'
                                                : staff?.employment_status === 'TERMINATED' ? '#dc2626' : '#16a34a',
                                        }}
                                    />
                                    {staff?.employment_sequence > 1 && (
                                        <Chip label={`Rehire #${staff.employment_sequence}`} size="small" sx={{ fontWeight: 800, bgcolor: '#ede9fe', color: '#5b21b6' }} />
                                    )}
                                </Stack>
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tenure (This Period)</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#334155', mt: 0.5 }}>{singleTenure.formatted}</Typography>
                            </Box>
                            {staff?.employment_sequence > 1 && (
                                <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cumulative Tenure</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.dark', mt: 0.5 }}>{cumulativeTenure.formatted}</Typography>
                                </Box>
                            )}
                            <Box>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Joining Date</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 700, color: '#334155', mt: 0.5 }}>{staff?.joining_date ? formatDDMMYYYY(staff.joining_date) : '—'}</Typography>
                            </Box>
                            {(staff?.employment_status === 'RESIGNED' || staff?.employment_status === 'TERMINATED') && (
                                <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cessation Date</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#ef4444', mt: 0.5 }}>{staff?.cessation_date ? formatDDMMYYYY(staff.cessation_date) : '—'}</Typography>
                                </Box>
                            )}
                            <Box>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Employment Periods</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#334155', mt: 0.5 }}>{employmentChain.length > 0 ? employmentChain.length : 1}</Typography>
                            </Box>
                        </Stack>
                    </Box>

                    {/* ── Pending Approver Handover Alert ── */}
                    {pendingHandover.length > 0 && (
                        <Box sx={{
                            mb: 3, p: 2, borderRadius: 2,
                            bgcolor: '#fff7ed', border: '1.5px solid #f97316',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2
                        }}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box sx={{ fontSize: 22 }}>⚠️</Box>
                                <Box>
                                    <Typography sx={{ fontWeight: 800, color: '#c2410c', fontSize: 14 }}>
                                        Pending Approver Reassignment
                                    </Typography>
                                    <Typography sx={{ color: '#92400e', fontSize: 13 }}>
                                        This staff is still set as approver for <strong>{pendingHandover.length}</strong> employee(s). Please reassign before their last working day.
                                    </Typography>
                                </Box>
                            </Stack>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={async () => {
                                    const initAssign = {};
                                    pendingHandover.forEach(a => { initAssign[a.staff_id] = ''; });
                                    setHandoverList(pendingHandover);
                                    setHandoverAssignments(initAssign);
                                    setHandoverStep(true);
                                    setOffboardOpen(true);
                                    if (allStaffList.length === 0) {
                                        try {
                                            const res = await axios.get(
                                                `${process.env.REACT_APP_STAFF_API_URL}/staff/approver-candidates`,
                                                { withCredentials: true, headers: { "X-Tenant": tenant } }
                                            );
                                            setAllStaffList(res.data?.data || []);
                                        } catch { /* non-critical */ }
                                    }
                                }}
                                sx={{
                                    bgcolor: '#f97316', '&:hover': { bgcolor: '#ea6c0a' },
                                    borderRadius: 2, fontWeight: 800, whiteSpace: 'nowrap',
                                    textTransform: 'none', px: 2.5, flexShrink: 0
                                }}
                            >
                                Reassign Approvers Now
                            </Button>
                        </Box>
                    )}

                    {/* ── Demographics ── */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2, borderBottom: '1px solid #f1f5f9', pb: 1 }}>Demographics &amp; Identity</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                            <InfoItem icon={PersonIcon} label="Gender" value={staff?.gender ? staff.gender.charAt(0).toUpperCase() + staff.gender.slice(1).toLowerCase() : '—'} />
                            <InfoItem icon={EventNoteIcon} label="Date of Birth" value={staff?.date_of_birth ? formatDDMMYYYY(staff.date_of_birth) : '—'} />
                            <InfoItem icon={PersonIcon} label="NRC No (EN)" value={staff?.nrc_number_english || staff?.nrc_number || '—'} />
                            <InfoItem icon={PersonIcon} label="Father's Name" value={staff?.father_name || '—'} />
                            <InfoItem icon={PersonIcon} label="Marital Status" value={staff?.marital_status || '—'} />
                            <InfoItem icon={PersonIcon} label="Nationality" value={staff?.nationality || 'Burmese'} />
                        </Box>
                    </Box>

                    {/* ── Contact ── */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2, borderBottom: '1px solid #f1f5f9', pb: 1 }}>Contact &amp; Address</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                            <InfoItem icon={PersonIcon} label="Personal Phone" value={staff?.personal_phone || '—'} />
                            <InfoItem icon={PersonIcon} label="Emergency Contact" value={staff?.emergency_contact_name || '—'} />
                            <InfoItem icon={PersonIcon} label="Emergency Phone" value={staff?.emergency_contact_phone || '—'} />
                            <Box sx={{ gridColumn: 'span 3' }}>
                                <InfoItem icon={PersonIcon} label="Permanent Address" value={staff?.permanent_address || '—'} />
                            </Box>
                        </Box>
                    </Box>

                    {/* ── Employment Chain Summary (only if rehired) ── */}
                    {employmentChain.length > 1 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2, borderBottom: '1px solid #f1f5f9', pb: 1 }}>Employment Chain</Typography>
                            <Stack spacing={1.5}>
                                {employmentChain.map((p, pIdx) => {
                                    const isCurrent = p.staff_id === Number(id);
                                    const statusColor = p.employment_status === 'ACTIVE' ? '#16a34a' : p.employment_status === 'RESIGNED' ? '#d97706' : '#dc2626';
                                    const statusBg = p.employment_status === 'ACTIVE' ? '#dcfce7' : p.employment_status === 'RESIGNED' ? '#fef3c7' : '#fef2f2';
                                    return (
                                        <Paper key={p.staff_id} sx={{ p: 2, borderRadius: 3, border: `2px solid ${isCurrent ? '#e0e7ff' : '#f1f5f9'}`, bgcolor: isCurrent ? '#fafbff' : '#fff' }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a' }}>Employment #{p.employment_sequence || pIdx + 1}</Typography>
                                                    {isCurrent && <Chip label="Current" size="small" sx={{ fontSize: 10, fontWeight: 800, height: 18, bgcolor: 'primary.main', color: '#fff' }} />}
                                                    <Chip label={p.employment_status} size="small" sx={{ fontSize: 10, fontWeight: 800, height: 18, bgcolor: statusBg, color: statusColor }} />
                                                </Stack>
                                                <Stack direction="row" spacing={2}>
                                                    <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700 }}>ID: {p.employee_number || p.staff_code}</Typography>
                                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Joined: {p.joining_date ? formatDDMMYYYY(p.joining_date) : '—'}</Typography>
                                                    {p.cessation_date && <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 600 }}>Left: {formatDDMMYYYY(p.cessation_date)}</Typography>}
                                                </Stack>
                                            </Stack>
                                        </Paper>
                                    );
                                })}
                            </Stack>
                        </Box>
                    )}

                    {/* ── Unified Career Timeline Table ── */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2, borderBottom: '1px solid #f1f5f9', pb: 1 }}>Unified Career Milestones</Typography>
                        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: isSalaryVisible ? '1fr 0.8fr 1.2fr 1.2fr 1fr 1fr 0.9fr 1fr' : '1fr 0.8fr 1.2fr 1.2fr 1fr 1fr 0.9fr', p: '8px 12px', bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                {['Emp ID', 'Seq', 'Date Range', 'Role', 'Dept', 'Branch', 'Reason', ...(isSalaryVisible ? ['Salary'] : [])].map(h => (
                                    <Typography key={h} variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.05em' }}>{h}</Typography>
                                ))}
                            </Box>
                            {unifiedTimeline.length === 0 ? (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>No career milestones recorded.</Typography>
                                </Box>
                            ) : (
                                unifiedTimeline.map((item, idx) => (
                                    <Box key={item.id || idx} sx={{ display: 'grid', gridTemplateColumns: isSalaryVisible ? '1fr 0.8fr 1.2fr 1.2fr 1fr 1fr 0.9fr 1fr' : '1fr 0.8fr 1.2fr 1.2fr 1fr 1fr 0.9fr', p: '8px 12px', borderBottom: '1px solid #f8fafc', bgcolor: idx === 0 ? '#fafbff' : '#fff', '&:last-child': { borderBottom: 'none' } }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>{item.employee_number || '—'}</Typography>
                                        <Chip label={`#${item.employment_sequence || 1}`} size="small" sx={{ fontSize: 9, fontWeight: 800, height: 18, bgcolor: '#f1f5f9', color: '#475569', width: 'fit-content' }} />
                                        <Typography variant="caption" sx={{ color: '#64748b' }}>{formatDDMMYYYY(item.effective_from)} – {item.effective_to ? formatDDMMYYYY(item.effective_to) : '–'}</Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#334155' }}>{item.job_role_name || item.role_name || '—'}</Typography>
                                        <Typography variant="caption" sx={{ color: '#64748b' }}>{item.org_node_name || '—'}</Typography>
                                        <Typography variant="caption" sx={{ color: '#64748b' }}>{item.branch_name || '—'}</Typography>
                                        <Chip
                                            label={item.assignment_reason || 'ONBOARDING'}
                                            size="small"
                                            sx={{
                                                fontSize: 9, fontWeight: 800, height: 18, width: 'fit-content',
                                                bgcolor: item.assignment_reason === 'PROMOTION' ? '#dcfce7' : item.assignment_reason === 'TRANSFER' ? '#ede9fe' : item.assignment_reason === 'REHIRE' ? '#fef3c7' : item.assignment_reason === 'INCREMENT' ? '#fef3c7' : item.assignment_reason === 'DECREMENT' ? '#ede9fe' : '#f1f5f9',
                                                color: item.assignment_reason === 'PROMOTION' ? '#15803d' : item.assignment_reason === 'TRANSFER' ? '#6d28d9' : item.assignment_reason === 'REHIRE' ? '#92400e' : item.assignment_reason === 'INCREMENT' ? '#b45309' : item.assignment_reason === 'DECREMENT' ? '#6d28d9' : '#64748b',
                                            }}
                                        />
                                        {isSalaryVisible && (
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#334155', textAlign: 'right' }}>
                                                {item.basic_salary > 0 ? Number(item.basic_salary).toLocaleString() + ' K' : '—'}
                                            </Typography>
                                        )}
                                    </Box>
                                ))
                            )}
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* ─── Offboard Modal — Two-step ─── */}
            <Dialog
                open={offboardOpen}
                onClose={() => { if (!offboardLoading && !handoverSaving) { setOffboardOpen(false); setHandoverStep(false); } }}
                maxWidth={handoverStep ? 'md' : 'xs'}
                fullWidth
            >
                {!handoverStep ? (
                    // ── Step 1: Offboard Form ──
                    <>
                        <DialogTitle sx={{ fontWeight: 800, color: '#0f172a', pb: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Box sx={{ p: 0.8, bgcolor: '#fef2f2', borderRadius: 2, display: 'flex' }}>
                                    <OffboardIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                                </Box>
                                Offboard Staff
                            </Stack>
                        </DialogTitle>
                        <DialogContent>
                            <Stack spacing={2.5} sx={{ mt: 1 }}>
                                {offboardError && <Alert severity="error" sx={{ borderRadius: 2 }}>{offboardError}</Alert>}
                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                    This will close all active assignments and compensation records on the last working day.
                                    The staff will lose login access after that date.
                                </Typography>
                                <TextField
                                    select fullWidth label="Reason" size="small"
                                    value={offboardForm.cessation_reason}
                                    onChange={(e) => setOffboardForm({ ...offboardForm, cessation_reason: e.target.value })}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                >
                                    <MenuItem value="RESIGNED">Resigned</MenuItem>
                                    <MenuItem value="TERMINATED">Terminated</MenuItem>
                                </TextField>
                                <TextField
                                    fullWidth label="Last Working Day" type="date" size="small"
                                    value={offboardForm.cessation_date}
                                    onChange={(e) => setOffboardForm({ ...offboardForm, cessation_date: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                                <TextField
                                    fullWidth label="Notes (optional)" multiline rows={2} size="small"
                                    value={offboardForm.notes}
                                    onChange={(e) => setOffboardForm({ ...offboardForm, notes: e.target.value })}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Stack>
                        </DialogContent>
                        <DialogActions sx={{ p: 3, pt: 1 }}>
                            <Button onClick={() => setOffboardOpen(false)} sx={{ fontWeight: 700, color: '#64748b' }}>Cancel</Button>
                            <Button
                                variant="contained"
                                onClick={handleOffboard}
                                disabled={offboardLoading}
                                sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, borderRadius: 2, px: 3, fontWeight: 700, textTransform: 'none' }}
                            >
                                {offboardLoading ? 'Processing...' : 'Confirm Offboard'}
                            </Button>
                        </DialogActions>
                    </>
                ) : (
                    // ── Step 2: Approver Handover ──
                    <>
                        <DialogTitle sx={{ fontWeight: 800, color: '#0f172a', pb: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Box sx={{ p: 0.8, bgcolor: '#fff7ed', borderRadius: 2, display: 'flex' }}>
                                    <GroupIcon sx={{ color: '#f97316', fontSize: 20 }} />
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>⚠️ Approver Handover Required</Typography>
                                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                        {staff?.full_name} is an approver for {handoverList.length} staff member{handoverList.length !== 1 ? 's' : ''}. Please reassign before last day.
                                    </Typography>
                                </Box>
                            </Stack>
                        </DialogTitle>
                        <DialogContent>
                            <Stack spacing={2}>
                                {offboardError && <Alert severity="error" sx={{ borderRadius: 2 }}>{offboardError}</Alert>}

                                {/* Bulk assign row */}
                                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>Quick Assign All To:</Typography>
                                        <FormControl size="small" fullWidth>
                                            <InputLabel>Select Staff</InputLabel>
                                            <Select
                                                label="Select Staff"
                                                value=""
                                                onChange={(e) => handleBulkAssign(e.target.value)}
                                                sx={{ borderRadius: 2 }}
                                            >
                                                {allStaffList.filter(s => s.employment_status !== 'RESIGNED' && s.employment_status !== 'TERMINATED').map(s => (
                                                    <MenuItem key={s.user_id} value={s.user_id}>
                                                        {s.full_name} ({s.staff_code || s.username})
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Stack>
                                </Box>

                                {/* Per-row table */}
                                <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '40px 1fr 100px 1fr', p: '8px 12px', bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                        {['', 'Staff Member', 'Role', 'New Approver'].map(h => (
                                            <Typography key={h} variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', fontSize: '0.6rem' }}>{h}</Typography>
                                        ))}
                                    </Box>
                                    {handoverList.map((item) => (
                                        <Box key={item.staff_id} sx={{ display: 'grid', gridTemplateColumns: '40px 1fr 100px 1fr', p: '10px 12px', borderBottom: '1px solid #f1f5f9', alignItems: 'center', '&:last-child': { borderBottom: 'none' } }}>
                                            <Checkbox
                                                size="small"
                                                checked={!!handoverAssignments[item.staff_id]}
                                                onChange={(e) => {
                                                    if (!e.target.checked) {
                                                        setHandoverAssignments(prev => ({ ...prev, [item.staff_id]: '' }));
                                                    }
                                                }}
                                            />
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.full_name}</Typography>
                                                <Typography variant="caption" sx={{ color: '#94a3b8' }}>{item.staff_code}</Typography>
                                            </Box>
                                            <Chip
                                                label={item.approver_role}
                                                size="small"
                                                sx={{
                                                    fontWeight: 700, fontSize: 10,
                                                    bgcolor: item.approver_role === 'Manager' ? '#ede9fe' : item.approver_role === 'HR' ? '#dcfce7' : '#fff7ed',
                                                    color: item.approver_role === 'Manager' ? '#6d28d9' : item.approver_role === 'HR' ? '#15803d' : '#c2410c',
                                                }}
                                            />
                                            <FormControl size="small" fullWidth>
                                                <InputLabel sx={{ fontSize: 12 }}>Select New Approver</InputLabel>
                                                <Select
                                                    label="Select New Approver"
                                                    value={handoverAssignments[item.staff_id] || ''}
                                                    onChange={(e) => setHandoverAssignments(prev => ({ ...prev, [item.staff_id]: e.target.value }))}
                                                    sx={{ borderRadius: 2, fontSize: 13 }}
                                                >
                                                    {allStaffList.filter(s => s.employment_status !== 'RESIGNED' && s.employment_status !== 'TERMINATED').map(s => (
                                                        <MenuItem key={s.user_id} value={s.user_id} sx={{ fontSize: 13 }}>
                                                            {s.full_name} ({s.staff_code || s.username})
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    ))}
                                </Box>
                                <Typography variant="caption" sx={{ color: '#94a3b8' }}>You can skip individual rows — only checked/assigned rows will be saved.</Typography>
                            </Stack>
                        </DialogContent>
                        <DialogActions sx={{ p: 3, pt: 1 }}>
                            <Button
                                onClick={() => { setOffboardOpen(false); setHandoverStep(false); navigate('/staff'); }}
                                sx={{ fontWeight: 700, color: '#64748b' }}
                            >
                                Skip for Now
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleHandoverSave}
                                disabled={handoverSaving}
                                sx={{ bgcolor: '#f97316', '&:hover': { bgcolor: '#ea580c' }, borderRadius: 2, px: 3, fontWeight: 700, textTransform: 'none' }}
                            >
                                {handoverSaving ? 'Saving...' : `Save Reassignments`}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* ─── Disciplinary Add/Edit Dialog ─── */}
            <Dialog open={disciplinaryDialogOpen} onClose={() => setDisciplinaryDialogOpen(false)} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 600, p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>
                    {editingDisciplinary ? 'Edit Disciplinary Record' : 'Add Disciplinary Record'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Case Date"
                            InputLabelProps={{ shrink: true }}
                            value={disciplinaryForm.case_date}
                            onChange={(e) => setDisciplinaryForm({ ...disciplinaryForm, case_date: e.target.value })}
                            required
                        />
                        <TextField
                            select
                            fullWidth
                            label="Discipline Category"
                            value={disciplinaryForm.discipline_id}
                            onChange={(e) => setDisciplinaryForm({ ...disciplinaryForm, discipline_id: e.target.value })}
                            required
                        >
                            {disciplineLookups?.categories?.map(c => (
                                <MenuItem key={c.id} value={c.id}>{c.discipline_code}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            fullWidth
                            label="Severity Level"
                            value={disciplinaryForm.severe_level_id}
                            onChange={(e) => setDisciplinaryForm({ ...disciplinaryForm, severe_level_id: e.target.value })}
                            required
                        >
                            {disciplineLookups?.levels?.map(l => (
                                <MenuItem key={l.id} value={l.id}>{l.level_code}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            fullWidth
                            label="Issuing Officer"
                            value={disciplinaryForm.officer_id}
                            onChange={(e) => setDisciplinaryForm({ ...disciplinaryForm, officer_id: e.target.value })}
                        >
                            <MenuItem value="">Select Issuing Officer (Optional)</MenuItem>
                            {disciplineLookups?.officers?.map(o => (
                                <MenuItem key={o.id} value={o.id}>{o.full_name} ({o.staff_code})</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            fullWidth
                            label="Action Outcome"
                            value={disciplinaryForm.dsoutcome_id}
                            onChange={(e) => setDisciplinaryForm({ ...disciplinaryForm, dsoutcome_id: e.target.value })}
                            required
                        >
                            {disciplineLookups?.outcomes?.map(o => (
                                <MenuItem key={o.id} value={o.id}>{o.outcome_code}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            fullWidth
                            label="Case Description"
                            multiline
                            rows={3}
                            value={disciplinaryForm.case_desc}
                            onChange={(e) => setDisciplinaryForm({ ...disciplinaryForm, case_desc: e.target.value })}
                            required
                        />
                        <TextField
                            fullWidth
                            label="Remark / Internal Note"
                            value={disciplinaryForm.remark}
                            onChange={(e) => setDisciplinaryForm({ ...disciplinaryForm, remark: e.target.value })}
                        />
                        <Box sx={{ p: 2, border: '2px dashed #e2e8f0', borderRadius: 4, textAlign: 'center', bgcolor: '#f8fafc' }}>
                            <input
                                type="file"
                                id="discipline-file"
                                hidden
                                onChange={(e) => setDisciplinaryForm({ ...disciplinaryForm, file: e.target.files[0] })}
                            />
                            <label htmlFor="discipline-file">
                                <IconButton component="span" sx={{ bgcolor: '#fff', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', mb: 1 }}>
                                    <UploadIcon color="primary" />
                                </IconButton>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>
                                    {disciplinaryForm.file ? disciplinaryForm.file.name : (editingDisciplinary?.dms_id ? 'Replace existing document' : 'Attach supporting document')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>PDF, JPG, or PNG (Max 5MB)</Typography>
                            </label>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setDisciplinaryDialogOpen(false)} sx={{ fontWeight: 700, color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveDisciplinary} disabled={uploading} sx={{ borderRadius: 2, px: 4, fontWeight: 700 }}>
                        {uploading ? 'Saving...' : 'Save Record'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ─── Disciplinary Upload Document Dialog ─── */}
            <Dialog open={uploadDisciplineAttachmentOpen} onClose={() => setUploadDisciplineAttachmentOpen(false)} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 450, p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>Upload Supporting Document</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                        Select a file to upload and link as the supporting action file for this disciplinary record.
                    </Typography>
                    <Box sx={{ p: 3, border: '2px dashed #e2e8f0', borderRadius: 4, textAlign: 'center', bgcolor: '#f8fafc', mt: 1 }}>
                        <input
                            type="file"
                            id="discipline-quick-file"
                            hidden
                            onChange={(e) => setDisciplinaryForm({ ...disciplinaryForm, file: e.target.files[0] })}
                        />
                        <label htmlFor="discipline-quick-file">
                            <IconButton component="span" sx={{ bgcolor: '#fff', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', mb: 1 }}>
                                <UploadIcon color="primary" />
                            </IconButton>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>
                                {disciplinaryForm.file ? disciplinaryForm.file.name : 'Select file to upload'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>PDF, JPG, or PNG (Max 5MB)</Typography>
                        </label>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setUploadDisciplineAttachmentOpen(false)} sx={{ fontWeight: 700, color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleUploadDisciplineFile} disabled={uploading || !disciplinaryForm.file} sx={{ borderRadius: 2, px: 4, fontWeight: 700 }}>
                        {uploading ? 'Uploading...' : 'Upload & Link'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ─── Family Add/Edit Dialog ─── */}
            <Dialog open={familyDialogOpen} onClose={() => setFamilyDialogOpen(false)} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 500, p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>
                    {editingFamily ? 'Edit Family Member' : 'Add Family Member'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            label="Member Name"
                            value={familyForm.member_name}
                            onChange={(e) => setFamilyForm({ ...familyForm, member_name: e.target.value })}
                            required
                        />
                        <TextField
                            select
                            fullWidth
                            label="Relationship"
                            value={familyForm.relationship}
                            onChange={(e) => setFamilyForm({ ...familyForm, relationship: e.target.value })}
                            required
                        >
                            {['Mother', 'Father', 'Wife', 'Husband', 'Son', 'Daughter', 'Brother', 'Sister', 'Other'].map(r => (
                                <MenuItem key={r} value={r}>{r}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            fullWidth
                            type="date"
                            label="Date of Birth"
                            InputLabelProps={{ shrink: true }}
                            value={familyForm.dob}
                            onChange={(e) => setFamilyForm({ ...familyForm, dob: e.target.value })}
                        />
                        <TextField
                            fullWidth
                            label="Salutation (e.g. Mr, Mrs, Daw, U)"
                            value={familyForm.salutation}
                            onChange={(e) => setFamilyForm({ ...familyForm, salutation: e.target.value })}
                        />
                        <TextField
                            select
                            fullWidth
                            label="Gender"
                            value={familyForm.gender}
                            onChange={(e) => setFamilyForm({ ...familyForm, gender: e.target.value })}
                        >
                            <MenuItem value="">Select Gender</MenuItem>
                            <MenuItem value="Male">Male</MenuItem>
                            <MenuItem value="Female">Female</MenuItem>
                            <MenuItem value="Other">Other</MenuItem>
                        </TextField>
                        <TextField
                            select
                            fullWidth
                            label="Marital Status"
                            value={familyForm.marital_status}
                            onChange={(e) => setFamilyForm({ ...familyForm, marital_status: e.target.value })}
                        >
                            <MenuItem value="">Select Status</MenuItem>
                            <MenuItem value="Single">Single</MenuItem>
                            <MenuItem value="Married">Married</MenuItem>
                            <MenuItem value="Divorced">Divorced</MenuItem>
                            <MenuItem value="Widowed">Widowed</MenuItem>
                        </TextField>
                        <Stack direction="row" spacing={3}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={familyForm.is_working}
                                        onChange={(e) => setFamilyForm({ ...familyForm, is_working: e.target.checked })}
                                    />
                                }
                                label="Working"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={familyForm.is_studying}
                                        onChange={(e) => setFamilyForm({ ...familyForm, is_studying: e.target.checked })}
                                    />
                                }
                                label="Studying"
                            />
                        </Stack>
                        <TextField
                            fullWidth
                            label="Remarks / Notes"
                            multiline
                            rows={2}
                            value={familyForm.notes}
                            onChange={(e) => setFamilyForm({ ...familyForm, notes: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setFamilyDialogOpen(false)} sx={{ fontWeight: 700, color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveFamily} sx={{ borderRadius: 2, px: 4, fontWeight: 700, bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}>
                        Save Family Member
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cost Centre Allocations Dialog */}
            <Dialog open={openCostCentreDialog} onClose={() => setOpenCostCentreDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 800, color: '#0f172a' }}>Manage Cost Centre Allocations</DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                        Allocate the staff's payroll expenses across different cost centres. The total must equal 100%.
                    </Typography>

                    <Stack spacing={2}>
                        {ccAllocationsForm.map((item, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Cost Centre</InputLabel>
                                    <Select
                                        value={item.cost_centre_id || ''}
                                        label="Cost Centre"
                                        onChange={(e) => {
                                            const newForm = [...ccAllocationsForm];
                                            newForm[index].cost_centre_id = e.target.value;
                                            setCcAllocationsForm(newForm);
                                        }}
                                    >
                                        {costCentres.map(cc => (
                                            <MenuItem key={cc.id} value={cc.id}>{cc.name} ({cc.code})</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    label="Percentage (%)"
                                    type="number"
                                    size="small"
                                    sx={{ width: 120 }}
                                    value={item.allocation_percentage}
                                    onChange={(e) => {
                                        const newForm = [...ccAllocationsForm];
                                        newForm[index].allocation_percentage = e.target.value;
                                        setCcAllocationsForm(newForm);
                                    }}
                                />
                                <IconButton color="error" onClick={() => {
                                    const newForm = ccAllocationsForm.filter((_, i) => i !== index);
                                    setCcAllocationsForm(newForm);
                                }} disabled={ccAllocationsForm.length === 1}>
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        ))}
                        <Button
                            startIcon={<AddIcon />}
                            onClick={() => setCcAllocationsForm([...ccAllocationsForm, { cost_centre_id: '', allocation_percentage: 0 }])}
                            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                        >
                            Add Allocation
                        </Button>
                    </Stack>
                    <Box sx={{ mt: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2, display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Total Allocation:</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: ccAllocationsForm.reduce((sum, item) => sum + Number(item.allocation_percentage || 0), 0) === 100 ? '#10b981' : '#ef4444' }}>
                            {ccAllocationsForm.reduce((sum, item) => sum + Number(item.allocation_percentage || 0), 0)}%
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, px: 3, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setOpenCostCentreDialog(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
                    <Button onClick={handleSaveAllocations} variant="contained" sx={{ bgcolor: 'primary.main', color: '#fff', fontWeight: 700, borderRadius: 2, '&:hover': { bgcolor: 'primary.dark' } }}>
                        Save Allocations
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ─── Rehire Search Modal (launched from StaffList) ─── */}
            <RehireSearchModal
                open={rehireSearchOpen}
                onClose={() => setRehireSearchOpen(false)}
                tenant={tenant}
                onSelect={(s) => {
                    setRehireSearchOpen(false);
                    navigate('/staff/rehire', { state: { prefillStaffId: String(s.staff_id) } });
                }}
            />

            <Snackbar open={statusMsg.open} autoHideDuration={6000} onClose={() => setStatusMsg({ ...statusMsg, open: false })}>
                <Alert onClose={() => setStatusMsg({ ...statusMsg, open: false })} severity={statusMsg.severity} sx={{ width: '100%' }}>
                    {statusMsg.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default StaffDetails;
